import type { FileWithDuration } from '@components/ChooseMediaModal/ChooseFileUploadModal';
import { mediaDeletedEvent, mediaUpdatedEvent } from '@lib/events/eventBus';
import {
  convertUnfurlDataToUnfurlItem,
  getCollectionNameFromUnfurlData,
  isFacebookAdsMultipleData,
  isGoogleDriveFolderData,
  isYoutubeChannel,
  isYoutubeMultipleData,
  isYoutubePlaylist,
} from '@lib/external/unfurl';
import { getRelativeDirectory } from '@lib/files/drag-files';
import { checkForDuplicate, DuplicateCandidate } from '@lib/files/fingerprint';
import { isMobileViewport } from '@lib/hooks/sizing/useIsMobile';
import { getFileThumbnailUrl } from '@lib/hooks/useFileMetadataExtraction';
import {
  descendFolderPath,
  type FolderPath,
  type LibraryFolderEntry,
} from '@lib/library/folderPath';
import { createContext } from '@lib/mobx/context';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import { ApiError } from '@lib/network/api';
import {
  ensureLibraryFolders,
  LIBRARY_FOLDER_ERROR,
  listLibraryFiles,
} from '@lib/network/libraryFolders';
import { NOT_ENOUGH_CREDITS_CODE, NOT_ENOUGH_STORAGE_CODE } from '@lib/utils/ownerAffordability';
import { showToast } from '@lib/utils/toastmaster';
import { errorMessage, isAbortError, toError } from '@lib/utils/toError';
import { pluralize } from '@lib/utils/units';
import {
  ChunkActivityMemory,
  ChunkGapMemory,
  completedPartsShare,
  isDeterministicChunkFailure,
  uploadChunks,
} from '@lib/utils/upload-chunk';
import {
  retryUploadRequest,
  signalWithTimeout,
  UPLOAD_REQUEST_TIMEOUT_MS,
} from '@lib/utils/upload-retry';
import { safeEncodeURI } from '@lib/utils/url';
import { AddMediaValidationService } from '@services/AddMediaValidationService';
import { debugStore } from '@stores/Debug';
import { LibraryFolderStore } from '@stores/Library/LibraryFolder';
import { LibraryManager } from '@stores/LibraryManager/LibraryManager';
import {
  AddMediaViaUrlNotification,
  notificationStore,
  UploadFileNotification,
} from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { ERROR_DURATION_THRESHOLD } from '@stores/UploadURL/mediaStatus';
import { userStore } from '@stores/User';
import { Media, MediaStatus } from '@typings/Media.js';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { now as mobxNow } from 'mobx-utils';
import pLimit from 'p-limit';
import PQueue from 'p-queue';
import { Video } from 'video-metadata-thumbnails';
import { IMetadata } from 'video-metadata-thumbnails/lib/video/imetadata';
import { type BatchFailures, type RejectedFile, summarizeBatchFailures } from './batchFailures';
import { findResumableUpload, type ResumableUpload } from './resumableUpload';
import { UploadURLOptions, UploadURLResult } from './types';
import {
  CONNECTION_FAILURE_REASON,
  isConnectivityFailure,
  UploadFailureCategory,
} from './uploadFailure';
import {
  appendBytesSample,
  appendProgressSample,
  computeBytesBasedUploadStats,
  computeGlobalUploadStats,
  latestTransferAdvanceMs,
  readSmoothedUploadStats,
  SmoothedUploadStats,
  updateSmoothedUploadStats,
  UploadBytesSample,
  UploadEtaState,
  UploadProgressSample,
} from './uploadStats';
import { readNetworkConnection, UploadTelemetry } from './uploadTelemetry';

export type { UploadFailureCategory } from './uploadFailure';

/**
 * Whether a media duration (in seconds) exceeds the 10-hour upload limit.
 * Shares `ERROR_DURATION_THRESHOLD` with the picker's `computeMediaStatus`, so
 * single/direct URL uploads enforce the same cap the picker shows as an error.
 */
function isOverDurationLimit(duration: number | undefined): boolean {
  return typeof duration === 'number' && duration > ERROR_DURATION_THRESHOLD;
}

/** Upload status metadata: basic file info + optional video metadata from Video.getMetadata() */
type UploadMetadata = Partial<IMetadata> & {
  name?: string;
  type?: string;
  size?: number;
  lastModified?: number;
};

// Common file metadata type to reduce repetition
interface FileMetadata {
  name: string;
  size: number;
  type: string;
  lastModified: number;
}

// Extended file metadata with file reference for internal use
interface ExtendedFileMetadata extends FileMetadata {
  file: File;
}

// Dragged files data structure
interface DraggedFilesData {
  total: number;
  files: FileMetadata[];
  byType: {
    video: ExtendedFileMetadata[];
    image: ExtendedFileMetadata[];
    audio: ExtendedFileMetadata[];
    other: ExtendedFileMetadata[];
  };
  summary: {
    total: number;
    video: number;
    image: number;
    audio: number;
    other: number;
  };
}

/** Size of each multipart part. Resuming an upload relies on cutting the file
 * exactly as the attempt that created its part URLs did. */
const UPLOAD_PART_SIZE = 20 * 1024 * 1024;
const MAXIMUM_CONCURRENT_UPLOADS = 4;
const MAXIMUM_CONCURRENT_PRE_UPLOADS = 4; // arrayBuffer + duplicate check per file
/**
 * Global cap on total in-flight chunk PUTs across ALL concurrent file uploads.
 * Without this, each file ran its own chunk pool (default 8), so up to
 * MAXIMUM_CONCURRENT_UPLOADS × 8 = 32 PUTs could hit the network at once,
 * saturating the uplink and starving each other into stall-timeouts. Tune here.
 */
const MAXIMUM_CONCURRENT_CHUNKS = 4;
const DEFAULT_UPLOAD_TOAST_DURATION = 10 * 1000; // in milliseconds
export const EXTERNAL_URL_BATCH_SIZE = 50;
export const EXTERNAL_URL_BATCH_SIZE_DEBUG = 2;
const EXTERNAL_URL_CONCURRENT_BATCHES = 2;

/**
 * Map backend error codes to user-facing reason + a stable category.
 * Consumers should match on `category` for logic; `reason` is for display only.
 */
function describeUploadFailure(error: unknown): {
  reason: string;
  category: UploadFailureCategory;
} {
  if (error instanceof ApiError) {
    if (error.code === NOT_ENOUGH_CREDITS_CODE) {
      return { reason: 'Not enough credits', category: 'credits' };
    }
    if (error.code === NOT_ENOUGH_STORAGE_CODE) {
      return { reason: 'Not enough storage', category: 'storage' };
    }
    if (error.code === LIBRARY_FOLDER_ERROR.notFound) {
      return { reason: 'Its folder no longer exists', category: 'other' };
    }
    return { reason: error.message || error.code, category: 'other' };
  }
  if (isConnectivityFailure(error)) {
    return { reason: CONNECTION_FAILURE_REASON, category: 'connection' };
  }
  if (error instanceof Error) {
    // A chunk PUT that exhausted its retries on a NON-connectivity cause (expired
    // presigned URL → 403, S3 500). Show a friendly reason rather than leaking the
    // raw "Failed to upload chunk N" wrapper, but keep it 'other' (not blamed on
    // the connection) so the batch telemetry stays accurate.
    if (/^Failed to upload chunk/i.test(error.message)) {
      return { reason: 'Upload failed', category: 'other' };
    }
    return { reason: error.message, category: 'other' };
  }
  return { reason: String(error), category: 'other' };
}

/**
 * A media-already-exists dedup conflict from a create-POST (start / external).
 * The Atlas backend returns HTTP 409 (`media.already_exists`) when a media with
 * the same `uniqueId` exists. It is deterministic — retrying the same request
 * will 409 again — so retries should bail immediately instead of looping.
 */
function isMediaConflictError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/** Backend statuses that mean an upload actually landed server-side (finish
 *  applied). A media in one of these is a real file — never a deletable orphan.
 *  The only definitively-safe-to-delete orphan is `'empty'` (start succeeded, no
 *  bytes committed, finish never happened); `'uploading'` may be a live/racing
 *  upload, and `'errored'`/`'cancelled'` are files the user may still act on. */
const LANDED_MEDIA_STATUSES: MediaStatus[] = ['queued', 'processing', 'completed'];

/**
 * Offline-park ceiling for the finish-verify GET. Far below the 120s default
 * because this GET runs INSIDE the outer finish retry's `verifySucceeded`: the
 * outer loop already parks up to 120s per attempt, so letting the inner one park
 * for another 120s (×3 attempts, ×3 outer attempts) compounds into ~24 minutes
 * stuck in an un-cancellable 'wrapping-up' state. The outer loop supplies the
 * patience; the verify just needs a quick answer.
 */
const VERIFY_FINISH_OFFLINE_WAIT_CEILING_MS = 5_000;

/**
 * How long a batch must go without a single byte advancing before the UI calls
 * it stalled. Bounded on both sides:
 * - Above the pauses a healthy upload legitimately takes (a chunk finishing
 *   while the next waits on the shared queue cap, or the tail of a part waiting
 *   on its S3 response). Byte-accurate progress emits roughly every 200ms while
 *   anything is moving, so a gap this long means genuinely nothing is moving.
 * - Below CHUNK_UPLOAD_BASE_IDLE_TIMEOUT_MS (45s), so the UI explains the pause
 *   before the chunk layer gives up on it and re-PUTs from byte 0. Telling the
 *   user only after the retry has already happened is too late to be useful.
 */
const BATCH_STALL_AFTER_MS = 12_000;

/**
 * The same question for the HEADLINE surfaces (library header, panel banner),
 * with a longer fuse. Deliberately not `BATCH_STALL_AFTER_MS`.
 *
 * The two surfaces answer to different costs. A batch card is a detail view the
 * user opened to watch one upload, so reacting in 12s is the point. The header
 * is ambient chrome carrying a countdown, and dropping that countdown is itself
 * a visible event — at 12s, a bursty link that pauses 15–20s per cycle (exactly
 * what the 45–90s adaptive leash is built to ride out) makes the header
 * alternate between "done in about 4 minutes" and "Uploading 3 files..." for
 * the entire upload. Flapping ambient chrome is worse than a countdown that is
 * briefly stale, so the headline waits until the pause has outlived what the
 * chunk layer treats as routine.
 *
 * Consequence to keep in mind: between 12s and 30s a batch card can read
 * "Waiting for a stable connection…" while the header still shows a countdown.
 * That is intended — see the population note on `globalUploadEta`.
 */
const HEADLINE_STALL_AFTER_MS = 30_000;

/** Which stage of the upload an error came from — drives the Sentry `phase` tag. */
export type UploadPhase = 'prepare' | 'chunks' | 'finish' | 'upload';

/**
 * Stamp the failing stage onto the error object (non-destructively — preserves
 * `instanceof ApiError` so the 402/404 branches still work). The innermost tag
 * wins: chunk/finish sites tag before the error propagates, so an outer default
 * never clobbers them.
 */
function tagUploadPhase<E>(error: E, phase: UploadPhase): E {
  if (error && typeof error === 'object' && !(error as { uploadPhase?: UploadPhase }).uploadPhase) {
    try {
      (error as { uploadPhase?: UploadPhase }).uploadPhase = phase;
    } catch {
      // Frozen error object — nothing we can do, leave it untagged.
    }
  }
  return error;
}

export type UploadFinishData = {
  partUrls: string[];
  opaqueData: {
    uploadId: string;
  };
};

export type UploadFileStatusString = 'queued' | 'uploading' | 'success' | 'error' | 'cancelled';

export type UploadDestination = {
  libraryId: string;
  /**
   * The library folder the upload is filed in. For a batch, the folder it was dropped on; a file
   * that arrived inside a dropped directory is filed below it (see `destinationForFile`).
   */
  folderPath: FolderPath;
};

/** Where one file of a batch is filed: the batch's folder, plus any directories it was dropped in. */
const destinationForFile = (destination: UploadDestination, file: File): UploadDestination => ({
  ...destination,
  folderPath: descendFolderPath(destination.folderPath, getRelativeDirectory(file)),
});

export type FileUploadStatus = {
  uuid: string;
  file: File;
  mediaId: string;
  status: UploadFileStatusString;
  destination: UploadDestination;
  progress: number; // 0 to 100
  uploadFinishData?: UploadFinishData;
  media?: Media;
  metadata?: UploadMetadata;
  abortController?: AbortController;
  fingerprint?: string;
  createdAt: string; // ISO timestamp when queued (stable for sorting)
  userMetadata?: Record<string, any>;
  localThumbnail?: string; // Client-generated thumbnail (in-memory only, not sent to backend)
  width?: number; // Client-side video dimensions for orientation detection before backend processing
  height?: number;
  /** Wall-clock ms when status first flipped to 'uploading'. Used as the speed
   * window's start until enough recent samples accumulate (see progressSamples),
   * and to keep stalls degrading the ETA. */
  uploadStartedAt?: number;
  /** Rolling buffer of recent progress samples (one per completed 20MB part),
   * age-pruned to the SPEED_WINDOW_MS speed window (see uploadStats.ts). Drives
   * the per-file windowed speed/ETA so a slow start or recovered stall doesn't
   * poison the estimate the way a lifetime average does. Transient + in-memory
   * only. */
  progressSamples?: UploadProgressSample[];
  /**
   * Wall-clock ms of the last time RAW bytes moved for this file. The stall
   * signal — "how long since anything moved" — which the measured speed cannot
   * provide: `computeBytesBasedUploadStats` anchors the window at
   * `uploadStartedAt` (progress 0), so a stalled file's speed decays toward zero
   * but never reaches it, and `speedBps === 0` therefore only ever means
   * "nothing has moved yet", never "movement stopped".
   *
   * Fed from two places: the positive-delta branch in `updateMediaProgress`
   * (aggregate progress advanced) and `noteUploadByteActivity` (raw movement
   * from the chunk layer's onByteActivity). The second matters during a retry
   * catch-up: the re-PUT climbs from byte 0 while the high-water-marked
   * aggregate holds still, and without the raw feed a healthy recovering
   * transfer would read as stalled for the whole catch-up. Unset until the
   * first movement.
   */
  lastProgressAtMs?: number;
  /** Progress-driven ETA/speed presentation state for per-file surfaces. */
  smoothedStats?: SmoothedUploadStats;
  /** Parts of `uploadFinishData.partUrls` whose PUT succeeded, in landing order.
   * What a retry of this upload skips (see ResumableUpload). */
  completedParts?: number[];
  /** Progress (0–100) this attempt started from: what an earlier attempt's parts
   * already covered. Undefined for an upload that started from 0. */
  resumedProgress?: number;
};

export class UploadManager extends ReactionManager {
  @observable
  uploadStatus: Record<string, FileUploadStatus> = {};

  @observable
  uploadIdArray: Array<string> = [];

  @observable
  addedURLs: Array<string> = [];

  @observable
  rejectedFiles: RejectedFile[] = [];

  /** Notification ids that the user just cancelled. uploadFiles() reads this
   * before firing its `upload-batch-complete` toast and skips the toast if the
   * id is present — otherwise the user sees both "Import cancelled" (from
   * cancelBatch) and "N files failed" (from the still-running uploadFiles
   * resolving its Promise.allSettled with N AbortErrors). Cleared the moment
   * uploadFiles consumes it. Not persisted. */
  private cancelledBatchIds = new Set<string>();

  @observable
  draggedFiles: Array<File> = [];

  @observable
  mediaIdToLocalUUID: Record<string, string> = {};

  /** Backend media deletions deferred while offline. Removing a failed upload
   * clears its row locally right away (works with no connection); the orphaned
   * backend media is deleted best-effort here and retried on the next `online`
   * event. If the tab closes first the orphan simply lingers server-side —
   * acceptable, it's a never-finished upload. */
  @observable
  pendingMediaDeletions: Array<{ libraryId: string; mediaId: string }> = [];

  /** Monotonic bytes-uploaded counter for the current upload session (an
   * upload session = from an idle state until uploads drain again). Fed by
   * updateMediaProgress deltas; survives files completing AND their
   * uploadStatus entries being cleared, which is what lets the global
   * banner/header ETA stay continuous across file completions — unlike the
   * per-file windows, where a finished file's speed contribution vanishes.
   * A whole-file retry restarting at a lower % re-counts its re-uploaded bytes
   * (the counter never decrements); that briefly overstates speed, which is
   * acceptable for a display estimate. Transient + in-memory only. */
  @observable
  private globalBytesUploaded = 0;

  /** Sample history of globalBytesUploaded, age-pruned to the speed window
   * (see uploadStats.ts). Seeded with a {t, bytes: 0} sample at session start
   * so warmup measures since-session-start. `.ref` — replaced wholesale by
   * appendBytesSample's immutable updates. */
  @observable.ref
  private globalBytesSamples: UploadBytesSample[] = [];

  /** Progress-driven presentation state. Unlike the raw estimator, its finish
   * timestamp naturally counts down between chunk-completion bursts. */
  @observable.ref
  private smoothedGlobalStats: SmoothedUploadStats | null = null;

  pLimit = pLimit(MAXIMUM_CONCURRENT_UPLOADS);
  private pLimitPreUpload = pLimit(MAXIMUM_CONCURRENT_PRE_UPLOADS);
  /** Shared across every file's chunk upload so total in-flight PUTs are globally bounded. */
  private chunkQueue = new PQueue({ concurrency: MAXIMUM_CONCURRENT_CHUNKS });
  /** Adaptive-leash memory for the chunk layer, deliberately the same scope as
   *  `chunkQueue` above: both describe the ONE connection every upload shares.
   *  Held here rather than inside a `uploadChunks` call so a newly started file
   *  inherits what earlier files already learned about the link — otherwise
   *  file 2 of a batch re-learns from the 45s floor, meets the same pause file 1
   *  survived, and fails in exactly the band the leash exists to cover. */
  private chunkGapMemory: ChunkGapMemory = { maxRecoveredGapMs: 0 };
  /** Connection-scoped "when did bytes last move" — shared with `chunkQueue` /
   *  `chunkGapMemory` so a chunk starved by ANOTHER file's parts is not mistaken
   *  for a stalled one. See ChunkActivityMemory. */
  private chunkActivityMemory: ChunkActivityMemory = { lastByteActivityAt: null };

  /** One surface for all upload observability — Sentry breadcrumbs, structured
   *  captures, and the resilience accumulator feeding the batch ROI counters.
   *  See uploadTelemetry.ts. */
  private telemetry = new UploadTelemetry();
  private pLimitDedupe = pLimit(1); // Serialize dedupe to avoid race with alreadyProcessedFromBatch

  private validationService: AddMediaValidationService;

  // Utility function to map file to metadata
  private mapFileToMetadata(file: File): FileMetadata {
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
    };
  }

  // Utility function to map file to extended metadata
  private mapFileToExtendedMetadata(file: File): ExtendedFileMetadata {
    return {
      ...this.mapFileToMetadata(file),
      file,
    };
  }

  // Utility function to filter and map files by type
  private getFilesByType(mimeType: string): ExtendedFileMetadata[] {
    return this.draggedFiles
      .filter((file) => file.type.startsWith(mimeType))
      .map((file) => this.mapFileToExtendedMetadata(file));
  }

  // Utility function to get other files (non-video, non-image, non-audio)
  private getOtherFiles(): ExtendedFileMetadata[] {
    return this.draggedFiles
      .filter(
        (file) =>
          !file.type.startsWith('video/') &&
          !file.type.startsWith('image/') &&
          !file.type.startsWith('audio/'),
      )
      .map((file) => this.mapFileToExtendedMetadata(file));
  }

  private filesByStatus(status: UploadFileStatusString) {
    return Object.values(this.uploadStatus).filter(
      (uploadStatus) => uploadStatus.status === status,
    );
  }

  @computed
  get queuedFiles() {
    return this.filesByStatus('queued');
  }

  @computed
  get uploadingFiles() {
    return this.filesByStatus('uploading');
  }

  @computed
  get succeededFiles() {
    return this.filesByStatus('success');
  }

  @computed
  get erroredFiles() {
    return this.filesByStatus('error');
  }

  /** Headline (banner/header) stats: speed from the session byte counter so
   * file completions and single-part files don't destabilise the countdown —
   * see computeGlobalUploadStats for the full rationale. */
  @computed
  private get _globalUploadStats() {
    const nowMs = mobxNow(500);
    const raw = computeGlobalUploadStats(
      [...this.queuedFiles, ...this.uploadingFiles],
      this.globalBytesUploaded,
      this.globalBytesSamples,
      nowMs,
    );
    return readSmoothedUploadStats(this.smoothedGlobalStats, raw, nowMs);
  }

  private updateGlobalStatsSmoothing(nowMs: number) {
    const raw = computeGlobalUploadStats(
      [...this.queuedFiles, ...this.uploadingFiles],
      this.globalBytesUploaded,
      this.globalBytesSamples,
      nowMs,
    );
    this.smoothedGlobalStats = updateSmoothedUploadStats(this.smoothedGlobalStats, raw, nowMs);
  }

  /** Start a fresh speed-measurement session when uploads begin from idle.
   * Call BEFORE the new batch's entries are queued — while a previous batch is
   * still active this is a no-op, so back-to-back batches share one continuous
   * session (their bytes belong to the same window). */
  @action
  private resetByteSessionIfIdle() {
    if (this.hasActiveUploads) return;
    this.globalBytesUploaded = 0;
    this.globalBytesSamples = [{ t: Date.now(), bytes: 0 }];
    this.smoothedGlobalStats = null;
  }

  /**
   * Headline ETA — computed the SAME way as the batch card: current remaining
   * bytes ÷ the smoothed speed. We deliberately do NOT read a separately-smoothed
   * completion timestamp: smoothing an absolute completion time (10s constant)
   * lagged badly on a speed recovery — showing e.g. ~2h while the real figure was
   * ~24m — and let the displayed "X left at Y" drift out of sync with the shown
   * speed (they no longer multiplied to the remaining bytes). Dividing fresh
   * remaining by the already-smoothed, stall-aware `globalUploadSpeedBps` stays
   * responsive AND stable AND self-consistent, and matches the per-batch card by
   * construction.
   */
  @computed
  get globalUploadEta(): UploadEtaState {
    const stats = this._globalUploadStats;
    // No stats at all ⇒ no file has been picked up yet.
    if (!stats) return { kind: 'not-started' };
    // Stall check BEFORE the speed test, and NOT off the smoothing collapse it
    // once used: the smoothed speed decays during a stall without reaching zero
    // for ~30s+ (readSmoothedUploadStats only collapses past its
    // stallThreshold), so testing speed first kept the header counting down
    // confidently well into a dead link.
    //
    // Shares `hasStalled` with the per-batch detector so the definition of a
    // stall — clock, comparison, quiet-file handling — has one home. It does
    // NOT follow that the two can never disagree: they differ in POPULATION
    // (this one spans every in-flight file; isBatchStalled spans one batch's
    // uuids) and in THRESHOLD (see HEADLINE_STALL_AFTER_MS). A stalled batch
    // card under a still-counting header is therefore reachable and correct —
    // bytes really are moving, just not in that batch.
    const inFlight = [...this.queuedFiles, ...this.uploadingFiles];
    if (this.hasStalled(inFlight, HEADLINE_STALL_AFTER_MS)) return { kind: 'stalled' };
    if (stats.speedBps > 0) {
      return { kind: 'estimate', seconds: this.remainingBytes(inFlight) / stats.speedBps };
    }
    // Speed reads 0 and we're not stalled: nothing has moved yet (lastAdvanceMs
    // null) or bytes moved so recently the estimator hasn't caught up.
    return { kind: 'warming-up' };
  }

  /** Average upload throughput in bytes/sec across all in-flight files. Smoothed
   *  and stall-aware (0 during a stall) — see `_globalUploadStats`. 0 when there
   *  is no measurement; callers only ever ask `> 0`, and the not-started /
   *  warming-up / stalled distinction lives in `globalUploadEta` rather than in a
   *  second sentinel here. */
  @computed
  get globalUploadSpeedBps(): number {
    return this._globalUploadStats?.speedBps ?? 0;
  }

  /** Sum of not-yet-uploaded bytes across the given in-flight files (each file's
   *  size weighted by how much of it is left). Shared by the headline ETA and the
   *  per-batch card so both measure "remaining" identically. */
  private remainingBytes(files: FileUploadStatus[]): number {
    return files.reduce(
      (sum, f) => sum + f.file.size * (1 - Math.min(1, Math.max(0, (f.progress || 0) / 100))),
      0,
    );
  }

  /**
   * Per-batch {speedBps, etaSeconds} — pass the notification's `uploadUuids` to
   * filter. Returns null if none of the batch's files are currently in-flight.
   *
   * Speed is the smoothed GLOBAL throughput (`globalUploadSpeedBps`), not a
   * separate per-file-sample measurement, so this batch's countdown agrees with
   * the panel banner: a single upload session shares one bandwidth pool, and a
   * second independent speed+smoothing pass here just produced a divergent ETA
   * for the same bytes. ETA = this batch's remaining bytes ÷ that shared speed.
   */
  getBatchUploadStats(uploadUuids: string[]): { speedBps: number; etaSeconds: number } | null {
    if (uploadUuids.length === 0) return null;
    const wanted = new Set(uploadUuids);
    const files = [...this.queuedFiles, ...this.uploadingFiles].filter((f) => wanted.has(f.uuid));
    if (files.length === 0) return null;

    const speedBps = this.globalUploadSpeedBps;
    // Global speed not yet known / stalled (readSmoothedUploadStats collapses it to
    // 0 on a stall) → no ETA; the card shows "Calculating ETA…".
    if (!(speedBps > 0)) return { speedBps: 0, etaSeconds: Infinity };

    return { speedBps, etaSeconds: this.remainingBytes(files) / speedBps };
  }

  /**
   * Throughput measured from the batch's OWN files (via computeBytesBasedUploadStats),
   * used ONLY to derive the batch STATE in `deriveBatchState`:
   *   null            → no file in the batch has started (waiting for a slot) → 'queued'
   *   speedBps === 0  → nothing has moved YET → 'preparing'
   *   speedBps > 0    → the batch is transferring, or was and has stalled —
   *                     this cannot tell those apart, see `isBatchStalled`
   * `getBatchUploadStats` (global speed) can't make these distinctions — it reports
   * the whole session's speed for any batch with in-flight files — so it's the wrong
   * signal for the state machine. That one is for the ETA DISPLAY; this one is for
   * "is THIS batch actually moving right now?".
   */
  getBatchActivityStats(uploadUuids: string[]): { speedBps: number; etaSeconds: number } | null {
    if (uploadUuids.length === 0) return null;
    const wanted = new Set(uploadUuids);
    const files = [...this.queuedFiles, ...this.uploadingFiles].filter((f) => wanted.has(f.uuid));
    return computeBytesBasedUploadStats(files, mobxNow(500));
  }

  /**
   * The single definition of "these files were transferring and have gone
   * quiet". Both stall detectors route through here so the clock, the
   * comparison, and the treatment of finish-awaiting files can only ever be
   * changed in one place; callers supply the population and the threshold,
   * which are the only things that legitimately differ between them.
   *
   * The trap it encodes: a stalled file does NOT report `speedBps === 0`.
   * `computeBytesBasedUploadStats` anchors its window at `uploadStartedAt`
   * (progress 0) whenever no older sample qualifies, so `windowBytes` stays
   * `progress - 0 > 0` and the speed merely DECAYS as elapsed time grows. That
   * makes `speedBps === 0` reachable only when nothing has ever moved — it can
   * never mean "movement stopped". Deriving a stall from it produces a branch
   * that looks right and is provably dead.
   *
   * Hence elapsed-since-last-advance (`lastProgressAtMs`), over MID-transfer
   * files only — `latestTransferAdvanceMs` drops anything at 100%, since a file
   * awaiting its finish POST is quiet by design and its aging clock says
   * nothing about the connection. False when nothing has moved yet: that is
   * 'preparing', not stalled.
   */
  private hasStalled(files: FileUploadStatus[], thresholdMs: number): boolean {
    const lastAdvanceMs = latestTransferAdvanceMs(files);
    if (lastAdvanceMs == null) return false;
    return mobxNow(500) - lastAdvanceMs >= thresholdMs;
  }

  /**
   * Is some file in this batch mid-transfer and not moving? Answers the
   * question `getBatchActivityStats` structurally cannot — see `hasStalled`.
   *
   * Uses the short `BATCH_STALL_AFTER_MS` fuse: a batch card is a detail view
   * someone is actively watching, so it should react quickly. The headline
   * surfaces use a longer one (`HEADLINE_STALL_AFTER_MS`), so a card can report
   * a stall while the header is still counting down.
   */
  isBatchStalled(uploadUuids: string[]): boolean {
    if (uploadUuids.length === 0) return false;
    const wanted = new Set(uploadUuids);
    return this.hasStalled(
      [...this.queuedFiles, ...this.uploadingFiles].filter((f) => wanted.has(f.uuid)),
      BATCH_STALL_AFTER_MS,
    );
  }

  /**
   * Wall-clock start of the stall episode currently in progress, or null when the
   * connection is behaving. Non-observable — telemetry bookkeeping only, and
   * writing it from the reaction below must not feed back into a derivation.
   */
  private stallEpisodeStartedAt: number | null = null;

  /**
   * Turn the derived stall flag into a pair of one-shot events. Driven off
   * `globalUploadEta.kind === 'stalled'` rather than a fourth stall predicate, so
   * the telemetry episode is BY CONSTRUCTION the same event the library header
   * shows the user — including its `HEADLINE_STALL_AFTER_MS` fuse, which is the
   * right one here: the 12s batch fuse fires on hiccups the leash rides out
   * silently, and we want events per stall, not per hiccup.
   *
   * The `stallEpisodeStartedAt` guards make this idempotent, so it cannot double
   * count if the flag is ever recomputed without changing.
   */
  private handleStallFlagChange(stalled: boolean) {
    const inFlight = [...this.queuedFiles, ...this.uploadingFiles];
    if (stalled) {
      if (this.stallEpisodeStartedAt != null) return;
      this.stallEpisodeStartedAt = Date.now();
      // Attribute the episode to the same population the detector measured —
      // mid-transfer files. A file at 100% is quiet awaiting its finish POST, so
      // counting it would inflate stalledFileCount with files that never stalled.
      this.telemetry.noteStalled(
        inFlight.filter((f) => (f.progress || 0) < 100).map((f) => f.uuid),
      );
      trackingStore.uploadStalled({
        inFlightFileCount: inFlight.length,
        thresholdMs: HEADLINE_STALL_AFTER_MS,
        online: navigator.onLine,
        connectionEffectiveType: readNetworkConnection()?.effectiveType,
      });
      return;
    }
    const startedAt = this.stallEpisodeStartedAt;
    if (startedAt == null) return;
    this.stallEpisodeStartedAt = null;
    trackingStore.uploadStallEnded({
      stalledMs: Date.now() - startedAt,
      // Nothing left in flight means the flag dropped because the upload ended
      // (finished, failed, or cancelled) while still stalled — not because the
      // link came back. Same transition, opposite meaning.
      resolution: inFlight.length > 0 ? 'recovered' : 'upload-ended',
      inFlightFileCount: inFlight.length,
    });
  }

  /** True when at least one file in the batch has status === 'uploading'. */
  hasUploadingInBatch(uploadUuids: string[]): boolean {
    if (uploadUuids.length === 0) return false;
    const wanted = new Set(uploadUuids);
    return this.uploadingFiles.some((f) => wanted.has(f.uuid));
  }

  @computed
  get hasActiveUploads() {
    return this.uploadingFiles.length > 0 || this.queuedFiles.length > 0;
  }

  @computed
  get draggedFilesCount() {
    return this.draggedFiles.length;
  }

  @computed
  get hasDraggedFiles() {
    return this.draggedFiles.length > 0;
  }

  @action
  addDraggedFiles(files: File[]) {
    this.draggedFiles = [...this.draggedFiles, ...files];
  }

  @action
  clearDraggedFiles() {
    this.draggedFiles = [];
  }

  @action
  removeDraggedFile(file: File) {
    const index = this.draggedFiles.findIndex((f) => f === file);
    if (index > -1) {
      this.draggedFiles.splice(index, 1);
    }
  }

  getDraggedFiles(): File[] {
    return Array.from(this.draggedFiles);
  }

  getDraggedFilesByType(mimeType: string): File[] {
    return this.draggedFiles.filter((file) => file.type.startsWith(mimeType));
  }

  getDraggedVideoFiles(): File[] {
    return this.getDraggedFilesData().byType.video.map((f) => f.file);
  }

  getDraggedImageFiles(): File[] {
    return this.getDraggedFilesData().byType.image.map((f) => f.file);
  }

  getDraggedAudioFiles(): File[] {
    return this.getDraggedFilesData().byType.audio.map((f) => f.file);
  }

  getDraggedFilesAsString(): string {
    if (this.draggedFiles.length === 0) {
      return 'No dragged files';
    }
    const { files } = this.getDraggedFilesData();
    return files
      .map(
        (file) => `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB, type: ${file.type})`,
      )
      .join(', ');
  }

  getDraggedFilesData(): DraggedFilesData {
    if (this.draggedFiles.length === 0) {
      return {
        total: 0,
        files: [],
        byType: { video: [], image: [], audio: [], other: [] },
        summary: { total: 0, video: 0, image: 0, audio: 0, other: 0 },
      };
    }

    const files = this.draggedFiles.map((file) => this.mapFileToMetadata(file));
    const videos = this.getFilesByType('video/');
    const images = this.getFilesByType('image/');
    const audios = this.getFilesByType('audio/');
    const others = this.getOtherFiles();

    return {
      total: this.draggedFiles.length,
      files,
      byType: { video: videos, image: images, audio: audios, other: others },
      summary: {
        total: this.draggedFiles.length,
        video: videos.length,
        image: images.length,
        audio: audios.length,
        other: others.length,
      },
    };
  }

  constructor() {
    super();
    makeObservable(this);
    this.validationService = new AddMediaValidationService();
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('online', this.flushPendingMediaDeletions);
    // Stall telemetry. Via ReactionManager's `reaction` so it is disposed by
    // deinit(). The tracked expression re-evaluates on the 500ms `mobxNow` tick
    // inside globalUploadEta, but a reaction only runs its effect when the value
    // actually flips, so this is two events per episode rather than a firehose.
    //
    // The `hasActiveUploads` guard is load-bearing, not a fast path:
    // `_globalUploadStats` reads `mobxNow(500)` unconditionally, and mobx-utils'
    // `now()` only runs its timer while observed. Without the short-circuit this
    // reaction would observe it forever, keeping a 500ms tick — and a recompute of
    // the whole stats chain — alive on an idle tab that is not uploading anything.
    // It also makes 'upload-ended' the guaranteed close when the last file leaves
    // while stalled, instead of relying on the eta falling out of 'stalled'.
    this.reaction(
      () => this.hasActiveUploads && this.globalUploadEta.kind === 'stalled',
      (stalled) => this.handleStallFlagChange(stalled),
    );

    // Clear the local upload entry as soon as the backend confirms it has the
    // file (status past 'empty'). This used to live in `useStatusPillRefresh`,
    // which meant it only fired for media whose card happened to be on screen;
    // driving it off the status poll covers every media in the batch.
    //
    // It must stay per-media and immediate: if entries linger, ghost upload
    // cards reappear when the media is later deleted. `clearUploadForMedia`
    // only touches entries in the 'success' state, so this cannot race an
    // in-flight upload.
    this._reactions.push(
      mediaUpdatedEvent.on(({ media }) => {
        // One transaction for the whole emission: clearUploadForMedia rebuilds
        // uploadIdArray, so per-media actions would cost k array rebuilds and k
        // separate MobX flushes — i.e. k re-renders of the grid and panel for
        // what is logically one update.
        runInAction(() => {
          for (const file of media) {
            if (file.status !== 'empty') this.clearUploadForMedia(file._id);
          }
        });
      }),
    );
  }

  private async extractMetadataInMainThread(file: File, uuid: string) {
    try {
      const video = new Video(file);
      const videoMetadata = await video.getMetadata();
      runInAction(() => {
        const existing = this.uploadStatus[uuid]?.metadata;
        this.updateUploadStatusWithData(uuid, {
          metadata: { ...existing, ...videoMetadata },
        });
      });
    } catch (e) {
      // Non-video files or extraction failure: basic metadata already set in queueFileToUpload
    }
  }

  /** Warn before leaving while uploads are in flight. Stored as a field (stable
   *  reference) so deinit() can remove it — a local closure could not be. */
  private handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (this.hasActiveUploads) {
      e.preventDefault();
    }
  };

  override deinit() {
    super.deinit();
    window.removeEventListener('online', this.flushPendingMediaDeletions);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.uploadStatus = {};
    this.uploadIdArray = [];
    this.draggedFiles = [];
    this.globalBytesUploaded = 0;
    this.globalBytesSamples = [];
    this.smoothedGlobalStats = null;
  }

  private getIngestEndpoint(libraryId: string, route: string): string {
    return `/library/${libraryId}/media/${route}`;
  }

  private getFinishMediaUploadEndpoint(libraryId: string, mediaId: string): string {
    return `/library/${libraryId}/media/${mediaId}/upload/finish`;
  }

  @action
  updateMediaProgress(uuid: string, status: UploadFileStatusString, progress: number) {
    if (uuid in this.uploadStatus) {
      // Record a recent-speed sample only while actually transferring, so queued
      // 0% entries don't pollute the window's start. See computeBytesBasedUploadStats.
      const updates: Partial<FileUploadStatus> = { status, progress };
      let globalBytesAdvancedAt: number | null = null;
      let fileProgressAdvancedAt: number | null = null;
      if (status === 'uploading') {
        const prev = this.uploadStatus[uuid];
        const nowMs = Date.now();
        updates.progressSamples = appendProgressSample(prev.progressSamples, progress, nowMs);
        // A retry starts a fresh per-file measurement window. The regressed
        // progress sample is the new baseline; retaining the original start or
        // inventing a zero point at `nowMs` would count pre-reset bytes again.
        if (progress < prev.progress) {
          updates.uploadStartedAt = undefined;
          updates.smoothedStats = undefined;
        }
        // Feed the session byte counter (drives the global banner/header speed).
        // Negative deltas (whole-file retry) are skipped — the counter is
        // monotonic; see its declaration for the tradeoff.
        const deltaBytes = ((progress - prev.progress) / 100) * (prev.file?.size || 0);
        if (deltaBytes > 0) {
          fileProgressAdvancedAt = nowMs;
          // Same branch as the byte counter so the two can't disagree about
          // whether bytes moved. Drives the batch 'stalled' state.
          updates.lastProgressAtMs = nowMs;
          this.globalBytesUploaded += deltaBytes;
          this.globalBytesSamples = appendBytesSample(
            this.globalBytesSamples,
            this.globalBytesUploaded,
            nowMs,
          );
          globalBytesAdvancedAt = nowMs;
        }
      }
      this.updateUploadStatusWithData(uuid, updates);
      if (fileProgressAdvancedAt != null) {
        const current = this.uploadStatus[uuid];
        const raw = computeBytesBasedUploadStats([current], fileProgressAdvancedAt);
        current.smoothedStats =
          updateSmoothedUploadStats(current.smoothedStats ?? null, raw, fileProgressAdvancedAt) ??
          undefined;
      }
      if (globalBytesAdvancedAt != null) this.updateGlobalStatsSmoothing(globalBytesAdvancedAt);
    }
  }

  /**
   * Record raw byte movement from the chunk layer (see onByteActivity in
   * uploadChunks). Kept separate from updateMediaProgress on purpose: this must
   * fire even when the aggregate progress does NOT advance (retry catch-up
   * under the high-water mark), and it must not touch progress, samples, or
   * smoothing — it is purely the stall clock.
   */
  @action
  private noteUploadByteActivity(uuid: string) {
    const entry = this.uploadStatus[uuid];
    if (entry && entry.status === 'uploading') {
      entry.lastProgressAtMs = Date.now();
    }
  }

  @action
  async queueFileToUpload(file: File, destination: UploadDestination, uuid: string) {
    // Queue the file immediately without waiting for metadata (idempotent if already added by uploadFiles).
    this.resetByteSessionIfIdle();
    runInAction(() => {
      const existing = this.uploadStatus[uuid];
      this.uploadStatus[uuid] = {
        uuid,
        destination,
        status: 'queued',
        mediaId: '',
        progress: 0,
        file: file,
        abortController: new AbortController(),
        metadata: {
          name: file.name,
          type: file.type,
          size: file.size,
          lastModified: file.lastModified,
          ...(existing?.metadata?.duration != null ? { duration: existing.metadata.duration } : {}),
        },
        createdAt: existing?.createdAt ?? new Date().toISOString(),
        ...(existing?.userMetadata ? { userMetadata: existing.userMetadata } : {}),
        ...(existing?.localThumbnail ? { localThumbnail: existing.localThumbnail } : {}),
        ...(existing?.width != null ? { width: existing.width } : {}),
        ...(existing?.height != null ? { height: existing.height } : {}),
      };
      if (!this.uploadIdArray.includes(uuid)) {
        this.uploadIdArray.push(uuid);
      }
    });

    // Enrich with video metadata (duration, dimensions, etc.) when available
    this.extractMetadataInMainThread(file, uuid);
  }

  @action
  updateUploadStatus(uuid: string, status: UploadFileStatusString) {
    if (uuid in this.uploadStatus) {
      const prev = this.uploadStatus[uuid].status;
      this.uploadStatus[uuid].status = status;
      if (status === 'uploading' && prev !== 'uploading') {
        this.uploadStatus[uuid].uploadStartedAt = Date.now();
      }
    }
  }

  // Consolidated method for updating upload status with additional properties
  @action
  private updateUploadStatusWithData(uuid: string, updates: Partial<FileUploadStatus>) {
    if (uuid in this.uploadStatus) {
      const prev = this.uploadStatus[uuid].status;
      const next = {
        ...this.uploadStatus[uuid],
        ...updates,
      };
      if (next.status === 'uploading' && prev !== 'uploading' && next.uploadStartedAt == null) {
        next.uploadStartedAt = Date.now();
      }
      this.uploadStatus[uuid] = next;
    }
  }

  public getUploadStatus(uuid: string) {
    return this.uploadStatus[uuid];
  }

  public refreshLibrary(libraryId: string) {
    const libraryManager = window.libraryManager;
    if (libraryManager) {
      const library = libraryManager.libraryById(libraryId);
      if (library) {
        library.update();
      }
    }
  }

  public async uploadFiles(
    files: File[],
    destination: UploadDestination,
    source: 'file-picker' | 'drag-drop' = 'file-picker',
    /** Files rejected before the batch started (e.g. drag-drop validation rejects).
     * These are stamped with this batch's notificationId so they appear in the
     * batch-scoped Results modal alongside upload-phase failures. */
    preRejects?: Array<{ file: File; reason: string }>,
    /** Optional notification card title override. Defaults to the library name.
     * Retries pass custom strings like "Retry of all failed files". */
    notificationTitle?: string,
    /** Retries only: the earlier failed upload of each file, which it continues
     * when the server still has it waiting for parts. */
    resumable?: Map<File, ResumableUpload>,
  ) {
    const uploadStartTime = Date.now();
    const totalSizeBytes = files.reduce((sum, f) => sum + f.size, 0);
    trackingStore.uploadStarted({
      libraryId: destination.libraryId,
      fileCount: files.length,
      totalSizeBytes,
      source,
    });
    // Step 0: Toast and refresh so UI is responsive
    showToast('upload-queued', { files });
    this.refreshLibrary(destination.libraryId);

    // Step 1: Queue all files synchronously so uploading cards show immediately (no arrayBuffer yet).
    this.resetByteSessionIfIdle();
    const fileUuidPairs = files.map((file) => ({
      file,
      uuid: crypto.randomUUID(),
    }));
    runInAction(() => {
      fileUuidPairs.forEach(({ file, uuid }) => {
        // Prefer the 256px preview over the 4px LQIP (see getFileThumbnailUrl) — the
        // preview is what shows on the upload/processing cards, where the user expects
        // to recognise their file.
        const enriched = file as FileWithDuration;
        const thumbnail = getFileThumbnailUrl(file);

        this.uploadStatus[uuid] = {
          uuid,
          destination: destinationForFile(destination, file),
          status: 'queued',
          mediaId: '',
          progress: 0,
          file,
          abortController: new AbortController(),
          metadata: enriched.duration != null ? { duration: enriched.duration } : undefined,
          createdAt: new Date().toISOString(),
          ...(thumbnail ? { localThumbnail: thumbnail } : {}),
          ...(enriched.width != null ? { width: enriched.width } : {}),
          ...(enriched.height != null ? { height: enriched.height } : {}),
          ...(enriched.fingerprint ? { fingerprint: enriched.fingerprint } : {}),
        };
        this.uploadIdArray.push(uuid);
      });
    });

    // Create a notification for this upload batch (only when notification bell is active)
    let uploadNotificationId: string | null = null;
    let uploadProgressInterval: ReturnType<typeof setInterval> | null = null;

    if (debugStore.showUploadStatus) {
      const uploadLibraryName =
        window.libraryManager?.libraryById(destination.libraryId)?.data?.name || 'library';
      const pickThumbnail = (i: number) => {
        const f = files[i];
        return f ? getFileThumbnailUrl(f) : null;
      };
      const pickFingerprint = (i: number) => {
        const enriched = files[i] as FileWithDuration | undefined;
        return enriched?.fingerprint ?? null;
      };
      uploadNotificationId = notificationStore.add({
        type: 'upload-file',
        title: notificationTitle ?? uploadLibraryName,
        thumbnailUrl: pickThumbnail(0),
        secondaryThumbnailUrl: pickThumbnail(1),
        // Match the gradient seed the library grid uses for the same media
        // — VirtualUploadCard reads `status.fingerprint`, MediaCard reads
        // `file.uniqueId || file._id` (uniqueId == fingerprint per #680).
        gradientSeed: pickFingerprint(0),
        secondaryGradientSeed: pickFingerprint(1),
        isAudio: false,
        uploadProgress: 0,
        processingProgress: 0,
        uploadUuids: fileUuidPairs.map((p) => p.uuid),
        mediaIds: [],
        failedUploads: [],
        libraryId: destination.libraryId,
        folderPath: destination.folderPath,
        libraryName: uploadLibraryName,
        mediaTitles: files.map((f) => f.name),
        completedAt: null,
      });

      // Auto-open the notification popover so the user sees the upload
      // immediately — desktop only. On mobile the unified drawer deliberately
      // does NOT auto-open (it surfaces activity via the bell FAB badge), and
      // setPopoverOpen(true) there would mark the just-added batch read (hiding
      // it from the badge) and start polling behind a closed drawer.
      if (!isMobileViewport()) {
        notificationStore.setPopoverOpen(true);
      }

      // Periodically aggregate per-file upload progress into the notification.
      //
      // NOTE (workaround): the canonical fix for "interval leaks if an error is
      // thrown before clearInterval" would be a try/finally wrapping the rest of
      // uploadFiles(). That requires re-indenting ~380 lines of body, which blows
      // the PR size budget — so for now the interval self-terminates once every
      // file reaches a terminal state, with a hard tick-count safety net for the
      // rare case where an upstream error leaves files stuck in 'queued' and
      // processFile() never runs. See `upload-status-panel-gaps.md` for the
      // follow-up to swap this pattern for a proper try/finally.
      const totalFileCount = fileUuidPairs.length;
      const MAX_INTERVAL_TICKS = 14400; // 2 hours at 500ms — covers huge uploads, caps runaway
      let intervalTicks = 0;
      uploadProgressInterval = setInterval(() => {
        intervalTicks++;
        // If the user cancelled this batch, kill the aggregator immediately.
        // cancelBatch has already pushed the notification to its terminal
        // state via markBatchCancelled — but the per-file uploadStatus
        // entries are still alive while the async backend deletes settle,
        // and a tick during that window would re-aggregate them as in-flight
        // and stomp uploadProgress back to ~0, bouncing the BatchCard state
        // machine from 'cancelled-all' back to 'preparing' until the
        // post-await clearUpload calls finish.
        if (uploadNotificationId && this.cancelledBatchIds.has(uploadNotificationId)) {
          if (uploadProgressInterval) {
            clearInterval(uploadProgressInterval);
            uploadProgressInterval = null;
          }
          return;
        }
        runInAction(() => {
          // Weight each file's contribution to the batch percentage by its byte
          // size so a 1 GB file moves the bar more than a 1 MB thumbnail. The
          // file-count fallback below covers the degenerate all-zero-byte case
          // (e.g. empty files) where the byte-weighted formula would divide by 0.
          let completedCount = 0;
          let weightedBytes = 0;
          let failedBytes = 0;
          for (const { uuid, file } of fileUuidPairs) {
            const size = file.size;
            const s = this.uploadStatus[uuid];
            if (!s) {
              // Cleared entry = a succeeded upload the polling cleanup already removed.
              completedCount++;
              weightedBytes += size;
            } else if (s.status === 'error' || s.status === 'cancelled') {
              // Terminal-but-failed: counts toward the bar so it still completes,
              // but tracked separately so the UI can show it as a distinct (red)
              // slice rather than filling as if it succeeded.
              completedCount++;
              weightedBytes += size;
              failedBytes += size;
            } else if (s.status === 'success') {
              completedCount++;
              weightedBytes += size;
            } else {
              weightedBytes += (s.progress / 100) * size;
            }
          }
          const avgProgress =
            totalSizeBytes > 0 ? weightedBytes / totalSizeBytes : completedCount / totalFileCount;
          const failedProgress = totalSizeBytes > 0 ? failedBytes / totalSizeBytes : 0;
          notificationStore.updateUploadProgress(
            uploadNotificationId!,
            Math.min(avgProgress, 1),
            Math.min(failedProgress, 1),
          );
          if (
            (completedCount === totalFileCount || intervalTicks > MAX_INTERVAL_TICKS) &&
            uploadProgressInterval
          ) {
            clearInterval(uploadProgressInterval);
            uploadProgressInterval = null;
          }
        });
      }, 500);
    }

    // Adopt any pre-batch rejects (e.g. drag-drop validation rejects) into this
    // batch so they appear in the batch-scoped Results modal. When the upload
    // status panel is disabled (uploadNotificationId is null), they still land
    // in rejectedFiles untagged — matching the pre-panel library-scoped behavior.
    if (preRejects && preRejects.length > 0) {
      runInAction(() => {
        for (const r of preRejects) {
          this.rejectFailedFile(
            r.file,
            { reason: r.reason, category: 'other' },
            uploadNotificationId,
          );
        }
      });
    }

    // Fetch the library once: its files for duplicate detection, its folders so the ones a
    // dropped directory needs can be created before any file is filed in them.
    let existingFiles: Media[] = [];
    let existingFolders: LibraryFolderEntry[] = [];
    try {
      const listing = await listLibraryFiles(destination.libraryId);
      existingFiles = listing?.files || [];
      existingFolders = listing?.folders || [];
    } catch (e) {
      console.warn('Unable to fetch existing files for duplicate detection:', e);
    }

    // A file whose folder could not be created is refused by the ingest route and fails on its
    // own with "Its folder no longer exists", so nothing here has to route around it.
    const folderPathsNeeded = fileUuidPairs
      .map(({ file }) => destinationForFile(destination, file).folderPath)
      .filter((path) => path !== destination.folderPath);
    if (folderPathsNeeded.length > 0) {
      const { failed } = await ensureLibraryFolders(
        destination.libraryId,
        existingFolders,
        destination.folderPath,
        folderPathsNeeded,
        () => uploadNotificationId !== null && this.cancelledBatchIds.has(uploadNotificationId),
      );
      if (failed.length > 0) {
        console.error('Failed to create folders for dropped directories:', failed);
        showToast('folder-create-for-upload-failed', {
          count: failed.length,
          error: failed[0].error,
        });
      }
    }

    const stats = {
      uuidsToRemove: [] as string[],
      duplicateCount: 0,
      /** Files filtered out as duplicates — surfaced on the BatchCard once
       * the upload notification is created. `matchedMediaId` carries the
       * existing media's `_id` so the modal row can render a "View in
       * library" link; it may be undefined when the matched media was
       * present at fetch time but is missing now (race with another tab). */
      duplicateRows: [] as { filename: string; matchedMediaId: string | undefined }[],
      emptyMediaDeletedCount: 0,
    };
    const alreadyProcessedFromBatch: DuplicateCandidate[] = [];

    // Shared billing error for the batch — when set, remaining files skip without calling the API.
    let batchBillingError: ApiError | null = null;

    // Upload phase: prepareToUpload + uploadFileByChunks (existing concurrency limit).
    // Success results carry the mediaId + fileName inline so downstream code
    // (successTitles, transitionToProcessing, batch toast) doesn't re-read
    // them from `this.uploadStatus` — which can be cleaned up between a
    // file's individual success and Promise.allSettled returning (the poll
    // hooks call clearUploadForMedia as soon as the backend confirms, which
    // happens while siblings are still uploading).
    const processFile = (
      uuid: string,
      resume?: ResumableUpload,
    ): Promise<{
      success: boolean;
      uuid: string;
      error?: unknown;
      mediaId?: string;
      fileName?: string;
    }> =>
      this.pLimit(async () => {
        // Hoisted so it's still readable in the catch when the error path
        // already deleted uploadStatus[uuid] (404 handling, etc.).
        let fileName = '';
        // Fallback phase for the Sentry tag; chunk/finish failures tag the error
        // themselves and override this. Stays 'prepare' until prepare succeeds.
        let phase: UploadPhase = 'prepare';
        try {
          const uploadStatus = this.uploadStatus[uuid];
          if (!uploadStatus) {
            throw new Error(`Upload status not found for UUID: ${uuid}`);
          }
          // Stable snapshot — file.name doesn't change and `file` itself is
          // preserved across updateUploadStatusWithData spreads.
          fileName = uploadStatus.file.name;

          // If a billing error already occurred in this batch, skip without calling the backend.
          if (batchBillingError) {
            runInAction(() => {
              if (uuid in this.uploadStatus) {
                this.updateUploadStatus(uuid, 'error');
                this.rejectFailedFile(
                  this.uploadStatus[uuid].file,
                  describeUploadFailure(batchBillingError!),
                  uploadNotificationId,
                  uuid,
                );
              }
            });
            return { success: false, uuid, error: batchBillingError, fileName };
          }

          if (resume) {
            phase = 'upload';
            try {
              await this.uploadFileByChunks(this.uploadStatus[uuid]);
              return { success: true, uuid, mediaId: resume.media._id, fileName };
            } catch (error) {
              // An expired part URL or an aborted multipart session can't be
              // continued; start over the way a retry without a session does.
              if (!isDeterministicChunkFailure(error)) throw error;
              await this.abandonResumedUpload(uuid, resume);
              phase = 'prepare';
            }
          }

          const freshStatus = this.uploadStatus[uuid];
          if (!freshStatus) {
            throw new Error(`Upload status not found for UUID: ${uuid}`);
          }
          const { media, uploadFinishData } = await this.prepareToUpload(freshStatus);
          phase = 'upload';

          if (!uploadFinishData) {
            runInAction(() => {
              this.updateUploadStatusWithData(uuid, {
                status: 'success',
                uploadFinishData: undefined,
                media,
              });
            });
            return { success: true, uuid, mediaId: media._id, fileName };
          }

          runInAction(() => {
            // Prepared, but its chunks may sit behind the shared chunk-queue cap.
            // Stay "Queued for upload" until the first chunk actually gets a slot
            // (uploadFileByChunks' onStart flips it to "Uploading") — otherwise a
            // file waiting on the cap misleadingly shows "Uploading (0%)".
            this.updateUploadStatusWithData(uuid, {
              status: 'queued',
              uploadFinishData,
              media,
              completedParts: [],
            });
          });

          await this.uploadFileByChunks(this.uploadStatus[uuid]);
          return { success: true, uuid, mediaId: media._id, fileName };
        } catch (error) {
          console.warn(
            `[upload] "${fileName}" ✗ failed in phase ${phase}:`,
            error instanceof Error ? `${error.name}: ${error.message}` : error,
          );
          // Stop any of this file's OTHER chunks still churning in the shared
          // queue. Otherwise a failed file keeps the queue busy — sibling files
          // stay 'queued'/'uploading' so `hasActiveUploads` (and the "don't leave,
          // uploads in progress" prompt) stays on long after everything's dead —
          // and a late chunk-progress callback can even flip this file back to
          // 'uploading' right after we set 'error'. AbortError already aborted it.
          if (!isAbortError(error)) {
            this.uploadStatus[uuid]?.abortController?.abort();
          }
          if (isAbortError(error)) {
            runInAction(() => {
              this.updateUploadStatus(uuid, 'cancelled');
            });
          } else if (error instanceof ApiError && error.status === 404) {
            // Media was deleted (e.g. by another tab that re-uploaded the same file).
            // Silently discard — the user doesn't know this upload exists.
            console.warn(`Media for upload ${uuid} was deleted externally (404), discarding.`);
            runInAction(() => {
              if (uuid in this.uploadStatus) {
                // Must read mediaId before deleting uploadStatus since getMediaIdFromUuid reads from it
                const mediaId = this.getMediaIdFromUuid(uuid);
                delete this.uploadStatus[uuid];
                this.uploadIdArray = this.uploadIdArray.filter((id) => id !== uuid);
                if (mediaId) delete this.mediaIdToLocalUUID[mediaId];
              }
            });
          } else if (error instanceof ApiError && error.status === 402) {
            // Backend rejected upload due to insufficient credits or storage.
            // Set the flag so remaining files in this batch skip without calling the API.
            // The toast is shown once at batch end with the total failed count.
            batchBillingError = error;
            runInAction(() => {
              if (uuid in this.uploadStatus) {
                this.updateUploadStatus(uuid, 'error');
                this.rejectFailedFile(
                  this.uploadStatus[uuid].file,
                  describeUploadFailure(error),
                  uploadNotificationId,
                  uuid,
                );
              }
            });
          } else {
            const failure = describeUploadFailure(error);
            // Report the generic failure path — including exhausted chunk
            // retries / stall-timeouts and finish-call failures — which was
            // previously silent (no Sentry, no track event), leaving us blind.
            const status = this.uploadStatus[uuid];
            this.telemetry.reportUploadFailed({
              status,
              uuid,
              mediaId: status ? this.getMediaIdFromUuid(uuid) : undefined,
              fileName,
              error,
              failure,
              fallbackPhase: phase,
            });
            runInAction(() => {
              if (uuid in this.uploadStatus) {
                this.updateUploadStatus(uuid, 'error');
                this.rejectFailedFile(
                  this.uploadStatus[uuid].file,
                  failure,
                  uploadNotificationId,
                  uuid,
                );
              }
            });
          }
          return { success: false, uuid, error, fileName };
        }
      });

    // Pre-upload phase per file (concurrency limit): queue metadata + duplicate check.
    type PreUploadResult =
      | {
          uploadPromise: Promise<{
            success: boolean;
            uuid: string;
            error?: unknown;
            mediaId?: string;
            fileName?: string;
          }>;
        }
      | { skip: true };

    const preUploadOne = async (file: File, uuid: string): Promise<PreUploadResult> => {
      return this.pLimitPreUpload(async (): Promise<PreUploadResult> => {
        try {
          await this.queueFileToUpload(file, destinationForFile(destination, file), uuid);
        } catch (e) {
          return { skip: true };
        }

        // A retry continues the earlier upload's media, so the duplicate check
        // below must not see that media as an empty leftover and delete it.
        const resume = resumable?.get(file);
        if (
          resume &&
          (await this.isStillResumable(
            resume,
            destination.libraryId,
            this.uploadStatus[uuid]?.abortController?.signal,
          ))
        ) {
          if (!(uuid in this.uploadStatus)) return { skip: true };
          // Claim the media now rather than when an upload slot opens: until the
          // claim, the failed card still offers "Remove failed upload", and the
          // deferred delete removes any 'empty' media under the waiting retry.
          this.adoptResumableUpload(uuid, resume);
          alreadyProcessedFromBatch.push({
            _id: uuid,
            uniqueId: resume.fingerprint,
            metadata: { filename: file.name },
          });
          return { uploadPromise: processFile(uuid, resume) };
        }

        // Serialize dedupe to avoid race: two concurrent workers could both read alreadyProcessedFromBatch
        // before either has pushed, missing intra-batch duplicates.
        const dedupeResult = await this.pLimitDedupe(async () => {
          const candidates: DuplicateCandidate[] = [...existingFiles, ...alreadyProcessedFromBatch];
          let dup: Awaited<ReturnType<typeof checkForDuplicate>>;
          try {
            dup = await checkForDuplicate(file, candidates);
          } catch (e) {
            if (uuid in this.uploadStatus) {
              runInAction(() => {
                this.uploadStatus[uuid].fingerprint = undefined;
              });
            }
            return { proceed: true, dup: { isDuplicate: false, fingerprint: undefined } };
          }

          if (dup.isDuplicate && dup.matched) {
            const matchedMedia = existingFiles.find((f) => f._id === dup.matched!._id);
            if (matchedMedia && 'status' in matchedMedia && matchedMedia.status === 'empty') {
              try {
                // Re-check current status before deleting — the media may have
                // transitioned out of "empty" (e.g. another tab finished uploading)
                // between the initial fetch and now.
                let currentMedia: Media | undefined;
                try {
                  currentMedia = await userStore.apiWithAuth<Media>(
                    `/library/${destination.libraryId}/media/${matchedMedia._id}`,
                  );
                } catch (fetchError) {
                  if (fetchError instanceof ApiError && fetchError.status === 404) {
                    // Media was already deleted (e.g. by another tab) — proceed with upload.
                    console.log(
                      `Empty media ${matchedMedia._id} already deleted (404), proceeding with upload of ${file.name}`,
                    );
                  } else {
                    throw fetchError;
                  }
                }

                if (currentMedia && currentMedia.status !== 'empty') {
                  // Media is no longer empty — treat as a real duplicate.
                  stats.uuidsToRemove.push(uuid);
                  stats.duplicateCount++;
                  stats.duplicateRows.push({
                    filename: file.name,
                    matchedMediaId: dup.matched?._id,
                  });
                  return { proceed: false, dup };
                }

                // If currentMedia is undefined here (non-404 empty response), we skip
                // the delete and fall through to proceed with the upload — safe default.
                if (currentMedia) {
                  await retryUploadRequest(() =>
                    userStore.apiWithAuth(
                      `/library/${destination.libraryId}/media/${matchedMedia._id}`,
                      {
                        method: 'DELETE',
                        signal: signalWithTimeout(undefined, UPLOAD_REQUEST_TIMEOUT_MS),
                      },
                    ),
                  );
                  stats.emptyMediaDeletedCount++;
                  console.log(
                    `Deleted empty media ${matchedMedia._id} to allow reupload of ${file.name}`,
                  );
                  this.telemetry.breadcrumb('empty-media replaced (delete + reupload)', {
                    mediaId: matchedMedia._id,
                    file: file.name,
                  });
                }

                if (dup.fingerprint && uuid in this.uploadStatus) {
                  runInAction(() => {
                    this.uploadStatus[uuid].fingerprint = dup.fingerprint;
                  });
                }
                alreadyProcessedFromBatch.push({
                  _id: uuid,
                  uniqueId: dup.fingerprint,
                  metadata: { filename: file.name },
                });
                return { proceed: true, dup };
              } catch (deleteError) {
                // Downgraded from console.error: this is a dedup-path cleanup
                // miss, NOT an upload failure — but `captureConsoleIntegration`
                // turns every console.error into a Sentry *event*, so it used to
                // masquerade as a failure. Keep the story as a breadcrumb instead.
                console.warn(`Failed to delete empty media ${matchedMedia._id}:`, deleteError);
                this.telemetry.breadcrumb(
                  'empty-media delete failed (dedup cleanup)',
                  { mediaId: matchedMedia._id, message: errorMessage(deleteError) },
                  'warning',
                );
                stats.uuidsToRemove.push(uuid);
                stats.duplicateCount++;
                stats.duplicateRows.push({
                  filename: file.name,
                  matchedMediaId: dup.matched?._id,
                });
                return { proceed: false, dup };
              }
            } else {
              stats.uuidsToRemove.push(uuid);
              stats.duplicateCount++;
              stats.duplicateRows.push({
                filename: file.name,
                matchedMediaId: dup.matched?._id,
              });
              return { proceed: false, dup };
            }
          }

          if (dup.fingerprint && uuid in this.uploadStatus) {
            runInAction(() => {
              this.uploadStatus[uuid].fingerprint = dup.fingerprint;
            });
          }
          alreadyProcessedFromBatch.push({
            _id: uuid,
            uniqueId: dup.fingerprint,
            metadata: { filename: file.name },
          });
          return { proceed: true, dup };
        });

        if (!dedupeResult.proceed) {
          return { skip: true };
        }
        return { uploadPromise: processFile(uuid) };
      });
    };

    // Pre-upload phase completes for all files (bounded by pLimitPreUpload concurrency)
    const preUploadResults = await Promise.all(
      fileUuidPairs.map(({ file, uuid }) => preUploadOne(file, uuid)),
    );

    // Clean up duplicates now (after pre-upload phase, before waiting for uploads)
    runInAction(() => {
      stats.uuidsToRemove.forEach((uuid) => {
        if (uuid in this.uploadStatus) {
          delete this.uploadStatus[uuid];
          this.uploadIdArray = this.uploadIdArray.filter((id) => id !== uuid);
        }
      });
    });

    if (stats.emptyMediaDeletedCount > 0) {
      this.refreshLibrary(destination.libraryId);
    }
    if (stats.duplicateCount > 0) {
      // Attach the skipped rows to the batch notification so the
      // BatchCard's "View N duplicates skipped" affordance has something
      // to render. No-op when there's no notification (debug.showUploadStatus
      // off). `matchedMediaId` flows through to the modal's "View in
      // library" link.
      if (uploadNotificationId) {
        notificationStore.setSkippedDuplicates(
          uploadNotificationId,
          stats.duplicateRows.map((r, i) => ({
            key: `${uploadNotificationId}-dup-${i}`,
            filename: r.filename,
            libraryId: r.matchedMediaId ? destination.libraryId : undefined,
            mediaId: r.matchedMediaId,
          })),
        );
      }
      showToast('duplicates-skipped', { count: stats.duplicateCount });
    }

    // Extract upload promises (which have already started running under pLimit)
    const uploadPromises = preUploadResults
      .filter(
        (
          r,
        ): r is {
          uploadPromise: Promise<{
            success: boolean;
            uuid: string;
            error?: unknown;
            mediaId?: string;
            fileName?: string;
          }>;
        } => 'uploadPromise' in r,
      )
      .map((r) => r.uploadPromise);

    // Wait for all uploads to complete
    const uploadResults = await Promise.allSettled(uploadPromises);

    // Stop tracking upload progress and transition notification to processing phase
    if (uploadProgressInterval) {
      clearInterval(uploadProgressInterval);
      uploadProgressInterval = null;
    }
    if (uploadNotificationId) {
      // Preserve the failed (red) slice when marking the bar complete — passing
      // only progress=1 would default failedProgress to 0 and wipe the red slice
      // during the wrap-up transition for a batch that had failures. Recompute
      // from the final settled statuses (a cleared entry = a polling-cleaned
      // success; 'error'/'cancelled' = failed).
      let failedBytes = 0;
      for (const { uuid, file } of fileUuidPairs) {
        const s = this.uploadStatus[uuid];
        if (s?.status === 'error' || s?.status === 'cancelled') failedBytes += file.size;
      }
      const failedProgress = totalSizeBytes > 0 ? Math.min(failedBytes / totalSizeBytes, 1) : 0;
      notificationStore.updateUploadProgress(uploadNotificationId, 1, failedProgress);
    }

    // Only treat results with a resolved mediaId as successful — the
    // processFile success path always carries mediaId + fileName inline,
    // captured while uploadStatus was still live. Reading them off the
    // result rather than going back to `this.uploadStatus[uuid]` avoids a
    // race where a fast first-finished upload gets its status entry
    // cleared by polling (clearUploadForMedia) while siblings are still
    // uploading — which desynced successMediaIds and successTitles and
    // made the first card title show "Untitled".
    const successfulResults = uploadResults.filter(
      (
        r,
      ): r is PromiseFulfilledResult<{
        success: true;
        uuid: string;
        mediaId: string;
        fileName: string;
      }> =>
        r.status === 'fulfilled' &&
        r.value.success === true &&
        typeof r.value.mediaId === 'string' &&
        r.value.mediaId.length > 0,
    );
    const failedResults = uploadResults.filter(
      (r) => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success),
    );
    const nSuccess = successfulResults.length;
    const nFailed = failedResults.length;

    // Transition notification to processing phase with the successful media IDs
    if (uploadNotificationId) {
      const successMediaIds = successfulResults.map((r) => r.value.mediaId);
      const successTitles = successfulResults.map((r) => r.value.fileName || 'Untitled');
      if (successMediaIds.length > 0) {
        notificationStore.transitionToProcessing(
          uploadNotificationId,
          successMediaIds,
          successTitles,
        );
      } else {
        // All uploads failed — mark notification as complete
        notificationStore.updateProgress(uploadNotificationId, 1);
      }
    }

    const totalAttempted = uploadResults.length;

    // One pass classifies every failure into exactly one bucket, so the counts
    // always sum to nFailed (the Tracking sum invariant) whether a promise
    // resolved {success:false} or rejected. Billing (402) is split out because
    // it's handled inline by the BatchCard's BillingActionRow — the generic batch
    // summary toast only fires when there were NON-billing failures.
    let cancelledCount = 0;
    let billingFailedCount = 0;
    let deletedCount = 0;
    let connectivityFailedCount = 0;
    let otherFailedCount = 0;
    for (const r of failedResults) {
      const err = r.status === 'fulfilled' ? r.value.error : r.reason;
      if (isAbortError(err)) cancelledCount++;
      else if (err instanceof ApiError && err.status === 402) billingFailedCount++;
      else if (err instanceof ApiError && err.status === 404) deletedCount++;
      else if (isConnectivityFailure(err)) connectivityFailedCount++;
      else otherFailedCount++;
    }

    // Resilience ROI for the batch event: how often retry / offline-park /
    // finish-recovery saved an upload (a recovered upload throws no error, so
    // this is the only place that ROI is countable). Drained here — right after
    // the failure tally and BEFORE the throw-prone toast/notification work below —
    // by the batch's FULL uuid list (not just fulfilled results) so no file's
    // flags can leak into the long-lived map, even if a later step throws.
    const { retriedFileCount, offlineParkedFileCount, finishRecoveredCount, stalledFileCount } =
      this.telemetry.drainBatchResilience(fileUuidPairs.map((p) => p.uuid));

    // Show the generic batch summary only if there were non-billing failures.
    const hasNonBillingFailures = nFailed > billingFailedCount;
    // Filenames come from the processFile result payload (captured while
    // uploadStatus was still live); going back to this.uploadStatus here
    // would miss the same race as successTitles above.
    const firstSuccessFilename =
      nSuccess >= 1 ? successfulResults[0].value.fileName || undefined : undefined;
    const firstFailedFilename = (() => {
      if (nFailed === 0) return undefined;
      const firstFailed = failedResults[0];
      if (firstFailed.status === 'fulfilled') {
        return firstFailed.value.fileName || undefined;
      }
      // Promise rejected (no value payload) — fall back to erroredFiles.
      const firstErrored = this.erroredFiles[0];
      return firstErrored?.file.name;
    })();

    const hasActive = this.hasActiveUploads;
    const stillUploadingCount = hasActive
      ? this.uploadingFiles.length + this.queuedFiles.length
      : 0;
    const firstStillUploading = hasActive
      ? (this.uploadingFiles[0] ?? this.queuedFiles[0])
      : undefined;

    trackingStore.uploadBatchCompleted({
      libraryId: destination.libraryId,
      successCount: nSuccess,
      failedCount: nFailed,
      totalAttempted,
      durationMs: Date.now() - uploadStartTime,
      cancelledCount,
      billingFailedCount,
      deletedCount,
      connectivityFailedCount,
      otherFailedCount,
      retriedFileCount,
      offlineParkedFileCount,
      finishRecoveredCount,
      stalledFileCount,
    });

    // If the user cancelled this batch, cancelBatch() already showed the
    // `media-upload-cancelled` toast — skip our batch-complete toast so the
    // user doesn't see two contradictory messages back-to-back.
    const wasUserCancelled = uploadNotificationId
      ? this.cancelledBatchIds.delete(uploadNotificationId)
      : false;

    if (!wasUserCancelled && (hasNonBillingFailures || !batchBillingError)) {
      showToast('upload-batch-complete', {
        nSuccess,
        nFailed,
        totalAttempted,
        hasActiveUploads: hasActive,
        ...(firstSuccessFilename != null && { successFilename: firstSuccessFilename }),
        ...(firstFailedFilename != null && { failedFilename: firstFailedFilename }),
        ...(hasActive && {
          stillUploadingCount,
          stillUploadingFirstFilename: firstStillUploading?.file.name,
        }),
      });
    }

    this.refreshLibrary(destination.libraryId);
  }

  public async prepareToUpload(
    mediaUpload: FileUploadStatus,
    chunkSize: number = UPLOAD_PART_SIZE,
  ) {
    const { file, destination } = mediaUpload;
    const { libraryId } = destination;

    const numParts = Math.ceil(file.size / chunkSize);
    const startFileUploadEndpoint = this.getIngestEndpoint(libraryId, 'upload');

    // Carry uniqueId and dedup policy if available; use fingerprint directly
    const uniqueId: string | undefined = mediaUpload.fingerprint;

    // Retry the start call (park while offline). This POST creates a media and
    // is not idempotent: a retry after a lost-response success trips the
    // uniqueId dedup (onConflict: 'error') and fails — but that orphaned 'empty'
    // media is reclaimed by the empty-media dedup on the next re-upload, so the
    // failure is bounded and self-healing. Genuine transients recover here.
    const result = await retryUploadRequest(
      () =>
        userStore.apiWithAuth<{
          media: Media;
          uploadFinishData?: UploadFinishData;
        }>(startFileUploadEndpoint, {
          method: 'POST',
          body: {
            filename: file.name,
            size: file.size,
            parts: numParts,
            folderPath: destination.folderPath,
            // Server-side uniqueId constraint: 5-36 chars; our fingerprint-based id fits
            uniqueId,
            deduplicationPolicy: {
              onConflict: 'error',
            },
          },
          // Fresh per attempt: a hung start (black-hole link) times out and
          // retries instead of sticking the file at "Queued" forever.
          signal: signalWithTimeout(mediaUpload.abortController?.signal, UPLOAD_REQUEST_TIMEOUT_MS),
        }),
      {
        signal: mediaUpload.abortController?.signal,
        onWait: () => {
          console.warn(`[upload] start "${file.name}" offline — parking until connection returns`);
          this.telemetry.resilienceEvent(mediaUpload.uuid, 'offline-park', 'start', {
            file: file.name,
          });
        },
        onRetry: (attempt, delay, err) => {
          console.warn(
            `[upload] start "${file.name}" attempt ${attempt} failed, retrying in ${delay}ms — ${err.message}`,
          );
          this.telemetry.resilienceEvent(mediaUpload.uuid, 'retry', 'start', {
            file: file.name,
            attempt,
            message: err.message,
          });
        },
        // A 409 dedup conflict is deterministic — don't retry-loop it (see §8:
        // the media exists; resuming it needs upload/restart, a backend follow-up).
        isDeterministicFailure: isMediaConflictError,
      },
    );

    runInAction(() => {
      if (mediaUpload.uuid in this.uploadStatus) {
        this.updateMediaProgress(mediaUpload.uuid, 'queued', 0);
        this.mediaIdToLocalUUID[result.media._id] = mediaUpload.uuid;
      }
    });

    return result;
  }

  public async uploadExternalUrl(destination: UploadDestination, url: string) {
    const { libraryId, folderPath } = destination;
    // Note: Backend generates the same hash for external media URLs as we do on the frontend
    // (using the same algorithm). Duplicate detection happens in UrlImportComponent
    // using DuplicateDetectionService.filterDuplicates().
    try {
      const media = await userStore.apiWithAuth<Media>(
        this.getIngestEndpoint(libraryId, 'external'),
        {
          method: 'POST',
          body: {
            url: safeEncodeURI(url),
            folderPath,
          },
        },
      );
      return media;
    } catch (error) {
      // Billing failures (402) are surfaced by the BatchCard's
      // BillingActionRow inline on the failing batch.
      throw error;
    }
  }

  public async uploadExternalUrls(
    destination: UploadDestination,
    items: { url: string; name: string; userMetadata?: Record<string, any>; uniqueId?: string }[],
    options?: {
      onProgress?: (completed: number, total: number) => void;
      signal?: AbortSignal;
      /** Display name for the target library (e.g. for toast title). Callers with library context should pass this. */
      libraryName?: string;
    },
  ) {
    const { libraryId, folderPath } = destination;
    const allResults: Media[] = [];
    const errors: Array<{ batchIndex: number; error: Error; itemCount: number }> = [];
    const batchSize = debugStore.smallBatchSizeMode
      ? EXTERNAL_URL_BATCH_SIZE_DEBUG
      : EXTERNAL_URL_BATCH_SIZE;
    const onProgress = options?.onProgress;
    const signal = options?.signal;
    const libraryName = options?.libraryName;

    // Early return for empty items array to avoid division by zero
    if (items.length === 0) {
      return [];
    }

    const externalUploadStart = Date.now();
    trackingStore.externalUrlUploadStarted({ libraryId, urlCount: items.length });

    // Create all batches upfront
    const batches: Array<{ index: number; items: typeof items }> = [];
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push({
        index: Math.floor(i / batchSize),
        items: items.slice(i, i + batchSize),
      });
    }

    const totalBatches = batches.length;
    let completedBatches = 0;

    // Call progress callback immediately with initial state (0 completed, total known)
    onProgress?.(0, totalBatches);

    // Helper to update progress
    const updateProgress = () => {
      completedBatches++;
      onProgress?.(completedBatches, totalBatches);
    };

    // Shared billing error for the batch — when set, remaining batches skip without calling the API.
    let bulkBillingError: ApiError | null = null;

    // Process batches concurrently with pLimit
    const batchLimit = pLimit(EXTERNAL_URL_CONCURRENT_BATCHES);
    const batchPromises = batches.map(({ index, items: batchItems }) =>
      batchLimit(async () => {
        // Check for abort signal before processing
        if (signal?.aborted) {
          const abortError = new Error('Upload cancelled');
          abortError.name = 'AbortError';
          throw abortError;
        }

        // If a billing error already occurred, skip remaining batches.
        if (bulkBillingError) {
          errors.push({
            batchIndex: index,
            error: bulkBillingError,
            itemCount: batchItems.length,
          });
          updateProgress();
          return;
        }

        try {
          const media = await retryUploadRequest(
            async () => {
              // Check for abort signal before API call
              if (signal?.aborted) {
                const abortError = new Error('Upload cancelled');
                abortError.name = 'AbortError';
                throw abortError;
              }

              return await userStore.apiWithAuth<Media[]>(
                this.getIngestEndpoint(libraryId, 'external/bulk'),
                {
                  method: 'POST',
                  body: {
                    items: batchItems.map((item) => ({
                      url: safeEncodeURI(item.url),
                      name: item.name,
                      userMetadata: item.userMetadata,
                      ...(item.uniqueId && { uniqueId: item.uniqueId }),
                      folderPath,
                    })),
                  },
                  // User cancel + a per-attempt timeout so a hung request retries.
                  signal: signalWithTimeout(signal, UPLOAD_REQUEST_TIMEOUT_MS),
                },
              );
            },
            // A 409 dedup conflict on the batch is deterministic — bail instead of
            // retry-looping (the item(s) already exist server-side).
            { signal, isDeterministicFailure: isMediaConflictError },
          );
          // Avoid push(...media) to prevent "Maximum call stack size exceeded" for large batches.
          // Order is completion order, not batch order; that's fine since callers don't rely on it.
          for (const m of media) allResults.push(m);
          updateProgress();
        } catch (error) {
          const lastError = toError(error);

          // Don't treat abort errors as failures; skip progress update for aborts –
          // upload is being cancelled anyway and updating would cause a brief progress jump before the modal closes
          if (isAbortError(lastError) || signal?.aborted) {
            throw lastError;
          }

          // Billing errors (402) — set the flag so remaining batches skip.
          // The toast is shown once after all batches settle with the total failed count.
          if (error instanceof ApiError && error.status === 402) {
            bulkBillingError = error;
          }

          errors.push({
            batchIndex: index,
            error: lastError,
            itemCount: batchItems.length,
          });

          updateProgress();
        }
      }),
    );

    // Wait for all batches to complete (successful or failed)
    const results = await Promise.allSettled(batchPromises);

    // Check if any batch was aborted
    const aborted = results.some(
      (result) =>
        result.status === 'rejected' &&
        result.reason instanceof Error &&
        result.reason.name === 'AbortError',
    );

    // If aborted, throw abort error
    if (aborted || signal?.aborted) {
      const abortError = new Error('Upload cancelled');
      abortError.name = 'AbortError';
      throw abortError;
    }

    trackingStore.externalUrlUploadCompleted({
      libraryId,
      successCount: allResults.length,
      failedCount: errors.reduce((sum, e) => sum + e.itemCount, 0),
      durationMs: Date.now() - externalUploadStart,
    });

    // Billing failures are surfaced inline by the BatchCard's
    // BillingActionRow — no separate toast / rich-event is pushed.

    const hasNonBillingFailures = errors.some(
      (e) => !(e.error instanceof ApiError && e.error.status === 402),
    );

    // If all batches failed, show one accumulated toast then throw
    if (errors.length === totalBatches && allResults.length === 0) {
      const lastError = errors[errors.length - 1]?.error;
      const failedItems = errors.reduce((sum, e) => sum + e.itemCount, 0);
      if (hasNonBillingFailures) {
        showToast('library-add-failed', {
          error: lastError,
          libraryName,
          message: `${pluralize(failedItems, 'item')} failed to add. ${lastError?.message || 'An error occurred while adding media to your library.'}`,
        });
        // The external-URL import path had NO structured Sentry funnel (unlike
        // file uploads) — total failures were invisible except any incidental
        // console.error. Report the all-failed case so we're not blind to it.
        // Skip pure-billing failures (402) — those are expected + surfaced inline.
        this.telemetry.reportExternalFailed({
          libraryId,
          totalBatches,
          failedBatches: errors.length,
          failedItems,
          error: lastError,
        });
      }
      throw new Error(
        `All ${totalBatches} batch(es) failed. Last error: ${lastError?.message || 'Unknown error'}`,
        { cause: lastError },
      );
    }

    // If some batches failed, show one accumulated toast, log a warning, but don't throw (partial success)
    if (errors.length > 0) {
      const failedItems = errors.reduce((sum, e) => sum + e.itemCount, 0);
      const successfulItems = allResults.length;
      const lastError = errors[errors.length - 1]?.error;
      if (hasNonBillingFailures) {
        showToast('library-add-failed', {
          error: lastError,
          libraryName,
          message: `${pluralize(failedItems, 'item')} failed to add. ${lastError?.message || 'An error occurred while adding media to your library.'}`,
        });
      }
      console.warn(
        `${errors.length} of ${totalBatches} batch(es) failed during bulk upload. ` +
          `Successfully uploaded ${successfulItems} items, failed ${failedItems} items.`,
        errors,
      );
      // Partial failure — leave it as a breadcrumb (the successCount/failedCount
      // aggregate already goes to Mixpanel via externalUrlUploadCompleted); a
      // Sentry event per partial would be noisy for an otherwise-working import.
      this.telemetry.breadcrumb(
        'external-url import partial failure',
        { libraryId, failedBatches: errors.length, totalBatches, failedItems, successfulItems },
        'warning',
      );
    }

    // Create a processing notification for this batch (only when notification bell is active)
    if (allResults.length > 0 && debugStore.showUploadStatus) {
      const pickItemThumbnail = (i: number): string | null => {
        const t = items[i]?.userMetadata?.thumbnail;
        return typeof t === 'string' ? t : null;
      };
      const resolvedLibraryName = libraryName || 'library';
      notificationStore.add({
        type: 'add-media-via-url',
        title: resolvedLibraryName,
        thumbnailUrl: pickItemThumbnail(0),
        secondaryThumbnailUrl: pickItemThumbnail(1),
        gradientSeed: items[0]?.uniqueId ?? null,
        secondaryGradientSeed: items[1]?.uniqueId ?? null,
        isAudio: false,
        progress: 0,
        mediaIds: allResults.map((m) => m._id),
        libraryId,
        folderPath,
        libraryName: resolvedLibraryName,
        mediaTitles: items.map((item) => item.name),
        completedAt: null,
      });
    }

    return allResults;
  }

  public async uploadFileByChunks(
    fileUploadStatus: FileUploadStatus,
    chunkSize: number = UPLOAD_PART_SIZE,
  ) {
    if (!fileUploadStatus.uploadFinishData) {
      throw new Error('Upload finish data is not available');
    }
    // multi-part upload
    try {
      await uploadChunks(
        fileUploadStatus.uploadFinishData.partUrls,
        fileUploadStatus.file,
        chunkSize,
        (progress: number) => {
          if (fileUploadStatus.uuid in this.uploadStatus) {
            this.updateMediaProgress(fileUploadStatus.uuid, 'uploading', progress * 100);
          }
        },
        {
          signal: fileUploadStatus.abortController?.signal,
          sharedQueue: this.chunkQueue,
          gapMemory: this.chunkGapMemory,
          activityMemory: this.chunkActivityMemory,
          onStart: () => {
            // First chunk got a slot → flip "Queued for upload" to "Uploading",
            // from the share a resumed upload already had (0 for a fresh one).
            const entry = this.uploadStatus[fileUploadStatus.uuid];
            if (entry) {
              this.updateMediaProgress(fileUploadStatus.uuid, 'uploading', entry.progress);
            }
          },
          completedParts: new Set(fileUploadStatus.completedParts),
          onPartUploaded: (index) => this.recordUploadedPart(fileUploadStatus.uuid, index),
          // Raw byte movement, including what the high-water-marked progress
          // hides (a retry catch-up). Feeds the stall clock ONLY — display
          // progress still flows through the progressCallback above, so the
          // bar stays monotonic while the stall detector sees the truth.
          onByteActivity: () => this.noteUploadByteActivity(fileUploadStatus.uuid),
          onResilienceEvent: (kind, info) => {
            // A chunk fell back on the resilience layer — record it so a later
            // failure's Sentry report carries the story, and so the batch ROI
            // counters can measure how often retry/park saved an upload.
            this.telemetry.resilienceEvent(fileUploadStatus.uuid, kind, 'chunk', {
              file: fileUploadStatus.file.name,
              ...info,
            });
          },
        },
      );
    } catch (e) {
      throw tagUploadPhase(e, 'chunks');
    }
    try {
      await this.finishFileUpload(fileUploadStatus);
    } catch (e) {
      if (isAbortError(e)) {
        runInAction(() => {
          this.updateUploadStatus(fileUploadStatus.uuid, 'cancelled');
        });
        // Re-throw as an AbortError (not a generic Error) so the processFile
        // catch treats it as a cancellation and doesn't report it to Sentry.
        throw e;
      }
      throw tagUploadPhase(e, 'finish');
    }
  }

  public async finishFileUpload(fileUploadStatus: FileUploadStatus) {
    const { media, uploadFinishData } = fileUploadStatus;
    if (!media || !uploadFinishData) {
      throw new Error('Media or upload finish data is not available');
    }
    // finish upload
    try {
      const endPath = this.getFinishMediaUploadEndpoint(
        fileUploadStatus.destination.libraryId,
        media._id,
      );
      // Retry the finish call — a transient network blip here would otherwise
      // throw away a fully-uploaded file (all chunks already succeeded). Finish
      // is non-idempotent, so `verifySucceeded` checks the media's server-side
      // status after any error: the finish POST can land even when the client
      // never gets a clean response (lost reply / an "unlogged" backend pod), so
      // a landed-but-errored finish is recovered instead of re-fired or failed.
      await retryUploadRequest(
        () =>
          userStore.apiWithAuth(endPath, {
            method: 'POST',
            body: {
              opaqueData: uploadFinishData.opaqueData,
            },
            // Fresh per attempt: a hung finish times out and retries (verify then
            // recovers a landed-but-lost-response finish) instead of hanging.
            signal: signalWithTimeout(
              fileUploadStatus.abortController?.signal,
              UPLOAD_REQUEST_TIMEOUT_MS,
            ),
          }),
        {
          signal: fileUploadStatus.abortController?.signal,
          onWait: () => {
            console.warn(
              `[UploadManager] finish "${media._id}" offline — parking until connection returns`,
            );
            this.telemetry.resilienceEvent(fileUploadStatus.uuid, 'offline-park', 'finish', {
              mediaId: media._id,
            });
          },
          onRetry: (attempt, delay, err) => {
            console.warn(
              `[UploadManager] finish "${media._id}" attempt ${attempt} failed, retrying in ${delay}ms — ${err.message}`,
            );
            this.telemetry.resilienceEvent(fileUploadStatus.uuid, 'retry', 'finish', {
              mediaId: media._id,
              attempt,
              message: err.message,
            });
          },
          verifySucceeded: async () => {
            const finished = await this.confirmFinishSucceeded(
              fileUploadStatus.destination.libraryId,
              media._id,
              fileUploadStatus.abortController?.signal,
            );
            if (finished) {
              console.warn(
                `[UploadManager] finish "${media._id}" errored on the client but the media already finished server-side — treating as success`,
              );
              this.telemetry.reportFinishRecovered({
                uuid: fileUploadStatus.uuid,
                mediaId: media._id,
                fileName: fileUploadStatus.file.name,
                libraryId: fileUploadStatus.destination.libraryId,
              });
            }
            return finished;
          },
        },
      );

      // no-op: fingerprint is persisted via uniqueId on upload start
      runInAction(async () => {
        this.refreshLibrary(fileUploadStatus.destination.libraryId);
        this.updateUploadStatus(fileUploadStatus.uuid, 'success');
        // Refresh the library immediately when files are queued
      });
    } catch (e) {
      runInAction(() => {
        this.updateUploadStatus(fileUploadStatus.uuid, 'error');
      });
      throw e;
    }
  }

  /**
   * Was the finish actually applied server-side? The finish POST moves the media
   * out of 'empty', so any post-finish status is authoritative proof it landed —
   * even if the client's finish request errored (lost response / unlogged pod).
   * Returns false when the media is gone (404) or still unfinished, or when we
   * can't reach it to check.
   */
  private async confirmFinishSucceeded(
    libraryId: string,
    mediaId: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    // No offline short-circuit here, on purpose. It looked like a free win
    // ("offline, the GET can only fail — bail instead of parking"), but the
    // outer finish retry runs this check even on its LAST attempt, where there
    // is no next attempt to try again: bailing there turns a landed-but-lost-
    // response finish during an offline blip — the exact case this verify
    // exists to recover — into a reported failure. The 5s park ceiling below
    // already bounds what the short-circuit was saving.
    try {
      const media = await retryUploadRequest(
        () =>
          userStore.apiWithAuth<Media>(`/library/${libraryId}/media/${mediaId}`, {
            signal: signalWithTimeout(signal, UPLOAD_REQUEST_TIMEOUT_MS),
          }),
        {
          // Thread the caller's cancel signal so a user cancel interrupts the
          // verify GET's offline-park instead of hanging on to the ceiling.
          signal,
          // One attempt only: the OUTER finish retry is the retry loop here, and
          // it re-runs this check on each of its attempts. Retrying internally
          // would multiply out (see VERIFY_FINISH_OFFLINE_WAIT_CEILING_MS).
          maxRetries: 0,
          offlineWaitCeilingMs: VERIFY_FINISH_OFFLINE_WAIT_CEILING_MS,
          // A 404 means the media is gone — deterministic, don't retry it.
          isDeterministicFailure: (e) => e instanceof ApiError && e.status === 404,
        },
      );
      // "Landed" = any status PAST the transfer states. 'empty' is "start
      // succeeded, finish never happened"; 'uploading' is the backend mid-
      // transition — finish may have begun but has not completed, so it is NOT
      // proof it landed (the deferred-delete path and isFailedOrphanUpload both
      // treat a stale 'uploading' as a dead pre-finish upload; admitting it here
      // would mark the file uploaded while the media sits pre-processing
      // forever). Everything else — including 'errored'/'cancelled' — is a
      // post-finish state. Deliberately NOT LANDED_MEDIA_STATUSES: that list
      // answers a different question (which media are unsafe to delete as
      // orphans) and excludes 'errored'/'cancelled', which for THIS question
      // count as landed; treating them as "finish never ran" would re-fire the
      // non-idempotent finish POST and report a fully-uploaded file as failed.
      return !!media?.status && media.status !== 'empty' && media.status !== 'uploading';
    } catch {
      // 404 (media gone) or unreachable — cannot confirm; treat as not finished.
      return false;
    }
  }

  /**
   * Removes completed (success or error) uploads from the store.
   * When confirmedMediaIds is provided, only success entries whose media is in the set are removed;
   * error entries are always removed. This avoids clearing success until media is in the library and past "empty".
   */
  @action
  cleanupCompletedUploads(confirmedMediaIds?: Set<string>) {
    const keysToRemove: string[] = [];

    Object.entries(this.uploadStatus).forEach(([uuid, status]) => {
      if (status.status === 'error') {
        keysToRemove.push(uuid);
      } else if (status.status === 'success') {
        const mediaId = this.getMediaIdFromUuid(uuid);
        if (!confirmedMediaIds || (mediaId != null && confirmedMediaIds.has(mediaId))) {
          keysToRemove.push(uuid);
        }
      }
    });

    if (keysToRemove.length > 0) {
      runInAction(() => {
        keysToRemove.forEach((uuid) => {
          const mediaId = this.getMediaIdFromUuid(uuid);
          delete this.uploadStatus[uuid];
          if (mediaId) delete this.mediaIdToLocalUUID[mediaId];
        });
        this.uploadIdArray = this.uploadIdArray.filter((id) => !keysToRemove.includes(id));
      });
    }
  }

  /**
   * Validates an external media file (e.g. a Google Drive folder file) by its
   * reported MIME type / filename, using the same accept rules as local file
   * uploads. Public so the URL picker can re-validate selected folder files at
   * confirm time (the selected items don't carry a MIME type by then).
   */
  isAcceptedExternalMedia(args: { mimeType?: string; filename?: string }) {
    return this.validationService.fileUpload.isAcceptedMedia(args);
  }

  /**
   * Surfaces files skipped from a Google Drive folder import (unsupported type)
   * as a persistent rich-event card in the upload panel, mirroring the
   * drag-drop file-rejection card in useDragDropUpload.
   */
  pushFolderRejectedRichEvent(rejected: { filename: string; reason: string }[]) {
    if (rejected.length === 0) return;
    const isSingle = rejected.length === 1;
    const single = isSingle ? rejected[0] : null;
    notificationStore.pushRichEvent({
      type: 'rich-event',
      variant: 'error',
      iconKind: 'file-rejected',
      header: 'Some files were skipped',
      description: single
        ? `{filename} was skipped: ${single.reason}.`
        : `${rejected.length} files in the folder were skipped.`,
      descriptionFilename: single ? single.filename : undefined,
      descriptionLinkText: isSingle ? undefined : `${rejected.length} files`,
      rows: rejected.map((r, i) => ({
        key: `${r.filename}-${i}`,
        filename: r.filename,
        reason: r.reason,
      })),
      modalTitle: 'Skipped files',
      viewLinkAction: isSingle ? undefined : `View ${rejected.length} skipped files`,
    });
    trackingStore.uploadRichEventShown({
      kind: 'file-rejected',
      rowCount: rejected.length,
    });
  }

  @action
  uploadURL = async (
    options: UploadURLOptions,
    userIntent: 'single' | 'playlist' | null,
  ): Promise<UploadURLResult> => {
    const { url, library, unfurlData, isNewLibrary = false } = options;
    const destination: UploadDestination = {
      libraryId: library.id,
      folderPath: options.folderPath,
    };

    if (this.addedURLs.includes(url)) {
      return {
        success: false,
        error: 'This URL has already been added.',
      };
    }

    const formatValidation = this.validationService.url.validateURLFormat(url);
    if (!formatValidation.valid) {
      return {
        success: false,
        error: formatValidation.reason || 'Please enter a valid URL.',
      };
    }

    // Extract videos from unfurl data if available
    let createdMedia: Media[] = [];
    let firstThumbnail: string | null = null;
    let secondaryThumbnail: string | null = null;

    if (unfurlData) {
      const videos = convertUnfurlDataToUnfurlItem(unfurlData, url);
      firstThumbnail = videos[0]?.thumbnail ?? null;
      secondaryThumbnail = videos[1]?.thumbnail ?? null;

      // Check if it's a YouTube playlist or channel, or Facebook Ads Library
      if (isYoutubeMultipleData(unfurlData)) {
        const isPlaylist = isYoutubePlaylist(unfurlData);
        const isChannel = isYoutubeChannel(unfurlData);

        if (isPlaylist && userIntent !== 'single') {
          // adding playlist
          showToast('playlist-uploading', {
            count: videos.length,
            firstTitle: videos[0]?.title,
            libraryName: library.name,
          });
          // Process videos in parallel with Promise.all
          createdMedia = (
            await Promise.all(videos.map((video) => this.uploadExternalUrl(destination, video.url)))
          ).filter((m): m is Media => m != null);
          showToast('playlist-done');
        } else if (isChannel) {
          // adding channel
          showToast('channel-uploading', {
            count: videos.length,
            libraryName: library.name,
          });
          createdMedia = (
            await Promise.all(videos.map((video) => this.uploadExternalUrl(destination, video.url)))
          ).filter((m): m is Media => m != null);
          showToast('channel-done', {
            count: videos.length,
          });
        } else {
          // TODO: When isYoutubeMultipleData is true but neither playlist nor channel (e.g., future
          // YouTube types like search results), consider uploading extracted videos instead of the
          // original URL. See: https://github.com/ijw-fyi/web.front.v2/pull/550
          showToast('single-video-uploading');
          const media = await this.uploadExternalUrl(destination, url);
          if (media) createdMedia = [media];
          showToast('single-video-done');
        }
      } else if (isGoogleDriveFolderData(unfurlData)) {
        // Google Drive folder — like a playlist, add every file. A folder can
        // contain anything, so each file is validated against the same accepted
        // media types as file uploads; unsupported files are skipped and
        // surfaced as a rich-event card (mirroring drag-drop rejects). Kept as
        // its own branch (separate guard) so it can diverge from YouTube later.
        const accepted: typeof videos = [];
        const rejected: { filename: string; reason: string }[] = [];
        for (const video of videos) {
          const validation = this.validationService.fileUpload.isAcceptedMedia({
            mimeType: video.type,
            filename: video.title,
          });
          if (!validation.accepted) {
            rejected.push({
              filename: video.title || 'Untitled',
              reason: validation.reason || 'Unsupported file type',
            });
          } else if (isOverDurationLimit(video.duration)) {
            rejected.push({
              filename: video.title || 'Untitled',
              reason: 'Longer than 10 hours',
            });
          } else {
            accepted.push(video);
          }
        }

        if (rejected.length > 0) {
          this.pushFolderRejectedRichEvent(rejected);
        }

        if (accepted.length === 0) {
          // Nothing uploadable. Never fall back to adding the folder URL itself
          // (that's a Drive landing page, not media). Rejected files already
          // showed the skip card above; a truly empty folder is warned about
          // up front (disabled button + warning pill), so this is just a guard.
          return { success: false, error: 'This Google Drive folder is empty.' };
        } else {
          showToast('folder-uploading', {
            count: accepted.length,
            folderName: getCollectionNameFromUnfurlData(unfurlData, ''),
            libraryName: library.name,
          });
          createdMedia = (
            await Promise.all(
              accepted.map((video) => this.uploadExternalUrl(destination, video.url)),
            )
          ).filter((m): m is Media => m != null);
          trackingStore.googleDriveFolderImported({
            libraryId: library.id,
            fileCount: accepted.length,
            skippedCount: rejected.length,
            source: 'direct',
          });
          showToast('folder-done');
        }
      } else if (isFacebookAdsMultipleData(unfurlData)) {
        if (videos.length === 0) {
          // Fallback to single video upload for empty ads library
          showToast('single-video-uploading');
          const media = await this.uploadExternalUrl(destination, url);
          if (media) createdMedia = [media];
          showToast('single-video-done');
        } else {
          // adding Facebook Ads Library collection
          showToast('playlist-uploading', {
            count: videos.length,
            firstTitle: videos[0]?.title,
            libraryName: library.name,
          });
          // Process videos in parallel with Promise.all
          createdMedia = (
            await Promise.all(videos.map((video) => this.uploadExternalUrl(destination, video.url)))
          ).filter((m): m is Media => m != null);
          showToast('playlist-done');
        }
      } else {
        // Single video. Reject if it's over the 10-hour limit (only possible
        // when the unfurl gave us a duration to check).
        if (isOverDurationLimit(videos[0]?.duration)) {
          showToast('media-too-long');
          return { success: false, error: 'This media is longer than 10 hours.' };
        }
        showToast('single-video-uploading');
        // The item's url is the pasted link, or the file's own link for a share platform
        // (a Shade folder holding one video is added by its `?asset=` link, not the folder).
        const media = await this.uploadExternalUrl(destination, videos[0]?.url ?? url);
        if (media) createdMedia = [media];
        showToast('single-video-done');
      }
    } else {
      // No unfurl data - upload as single video (no duration to check here).
      showToast('single-video-uploading');
      const media = await this.uploadExternalUrl(destination, url);
      if (media) createdMedia = [media];
      showToast('single-video-done');
    }

    // Create a processing notification for this batch (only when notification bell is active)
    if (createdMedia.length > 0 && debugStore.showUploadStatus) {
      const videos = unfurlData ? convertUnfurlDataToUnfurlItem(unfurlData, url) : [];
      const mediaTitles = createdMedia.map(
        (m, i) => videos[i]?.title || m.metadata?.name || m.metadata?.filename || 'Untitled',
      );
      notificationStore.add({
        type: 'add-media-via-url',
        title: library.name,
        thumbnailUrl: firstThumbnail,
        secondaryThumbnailUrl: secondaryThumbnail,
        gradientSeed: videos[0]?.uniqueId ?? null,
        secondaryGradientSeed: videos[1]?.uniqueId ?? null,
        isAudio: false,
        progress: 0,
        mediaIds: createdMedia.map((m) => m._id),
        libraryId: library.id,
        folderPath: destination.folderPath,
        libraryName: library.name,
        mediaTitles,
        completedAt: null,
      });
    }

    // Remove URL from the added URLs after 10 minutes
    setTimeout(
      () => {
        this.addedURLs = this.addedURLs.filter((addedUrl) => addedUrl !== url);
      },
      10 * 60 * 1000,
    );

    return { success: true };
  };

  @action
  private recordUploadedPart(uuid: string, index: number) {
    const entry = this.uploadStatus[uuid];
    if (entry) (entry.completedParts ??= []).push(index);
  }

  /** The earlier failed upload of each file a retry is about to send, keyed by
   * the `File` the retry holds. */
  private resumableUploadsFor(files: File[], destination: UploadDestination) {
    const entries = Object.values(this.uploadStatus);
    const resumable = new Map<File, ResumableUpload>();
    for (const file of files) {
      const resume = findResumableUpload(entries, file, destinationForFile(destination, file));
      if (resume) resumable.set(file, resume);
    }
    return resumable;
  }

  /** The server must still be waiting for this upload's parts, and the user must
   * not have removed it (its deletion may still be queued). Anything else —
   * deleted, finished after all, or unreadable — goes through the ordinary
   * path, which already handles each case. */
  private async isStillResumable(
    resume: ResumableUpload,
    libraryId: string,
    signal?: AbortSignal,
  ): Promise<boolean> {
    const isDeletionQueued = () =>
      this.pendingMediaDeletions.some((p) => p.mediaId === resume.media._id);
    if (isDeletionQueued()) return false;
    try {
      const media = await userStore.apiWithAuth<Media>(
        `/library/${libraryId}/media/${resume.media._id}`,
        { signal: signalWithTimeout(signal, UPLOAD_REQUEST_TIMEOUT_MS) },
      );
      if (media?.status !== 'empty') {
        this.telemetry.breadcrumb('upload not resumed — media no longer empty', {
          mediaId: resume.media._id,
          status: media?.status,
        });
        return false;
      }
      // Again: the failed card could have been removed while the GET was out.
      return !isDeletionQueued();
    } catch (e) {
      const gone = e instanceof ApiError && e.status === 404;
      this.telemetry.breadcrumb(
        gone ? 'upload not resumed — media gone' : 'upload not resumed — media unreadable',
        { mediaId: resume.media._id, message: errorMessage(e) },
        gone ? 'info' : 'warning',
      );
      return false;
    }
  }

  /** Move the failed upload's media, part URLs and landed parts onto the retry's
   * entry, and drop the failed entry so the media maps to the live upload. */
  @action
  private adoptResumableUpload(uuid: string, resume: ResumableUpload) {
    const entry = this.uploadStatus[uuid];
    if (!entry) throw new Error(`Upload status not found for UUID: ${uuid}`);
    const progress =
      completedPartsShare(resume.completedParts, UPLOAD_PART_SIZE, entry.file.size) * 100;
    delete this.uploadStatus[resume.previousUuid];
    this.uploadIdArray = this.uploadIdArray.filter((id) => id !== resume.previousUuid);
    this.mediaIdToLocalUUID[resume.media._id] = uuid;
    this.updateUploadStatusWithData(uuid, {
      status: 'queued',
      fingerprint: resume.fingerprint,
      media: resume.media,
      uploadFinishData: resume.uploadFinishData,
      completedParts: [...resume.completedParts],
      progress,
      resumedProgress: progress,
    });
    this.telemetry.breadcrumb('upload resumed', {
      mediaId: resume.media._id,
      completedParts: resume.completedParts.length,
      totalParts: resume.uploadFinishData.partUrls.length,
    });
  }

  /** A resumed upload's session is gone: stop its parts, delete its media (whose
   * uniqueId would otherwise make the fresh start a 409) and reset the entry to
   * a fresh upload. The new abort controller is in place before the delete, so a
   * cancel during it still lands. */
  private async abandonResumedUpload(uuid: string, resume: ResumableUpload) {
    const abortController = new AbortController();
    runInAction(() => {
      this.uploadStatus[uuid]?.abortController?.abort();
      delete this.mediaIdToLocalUUID[resume.media._id];
      this.updateUploadStatusWithData(uuid, {
        status: 'queued',
        progress: 0,
        media: undefined,
        uploadFinishData: undefined,
        completedParts: undefined,
        resumedProgress: undefined,
        uploadStartedAt: undefined,
        progressSamples: undefined,
        smoothedStats: undefined,
        abortController,
      });
    });
    this.telemetry.breadcrumb('resumed upload session gone — starting over', {
      mediaId: resume.media._id,
    });
    const libraryId = this.uploadStatus[uuid]?.destination.libraryId;
    if (!libraryId) return;
    try {
      await retryUploadRequest(
        () =>
          userStore.apiWithAuth(`/library/${libraryId}/media/${resume.media._id}`, {
            method: 'DELETE',
            signal: signalWithTimeout(abortController.signal, UPLOAD_REQUEST_TIMEOUT_MS),
          }),
        { signal: abortController.signal },
      );
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 404)) throw e;
    }
  }

  getMediaIdFromUuid(uuid: string) {
    const uploadStatus = this.uploadStatus[uuid];
    if (!uploadStatus) return null;
    return uploadStatus.media?._id || uploadStatus.mediaId || null;
  }

  /**
   * Record a file that failed in the upload phase, twice over: in
   * `rejectedFiles`, which holds the File a retry needs and loses the entry once
   * the user retries or dismisses it, and on the batch notification, which keeps
   * the failure as part of the batch's outcome. Untagged (no batch) when the
   * upload panel is off. `uuid` is absent for files rejected before they were
   * queued, which never created a backend media.
   */
  @action
  private rejectFailedFile(
    file: File,
    failure: { reason: string; category: UploadFailureCategory },
    notificationId: string | null,
    uuid?: string,
  ): void {
    const mediaId = uuid ? this.getMediaIdFromUuid(uuid) : null;
    this.rejectedFiles.push({
      file,
      ...failure,
      ...(notificationId ? { notificationId } : {}),
      status: 'failed',
      ...(uuid && mediaId
        ? { createdMedia: { libraryId: this.uploadStatus[uuid].destination.libraryId, mediaId } }
        : {}),
    });
    if (notificationId) {
      notificationStore.recordFailedUpload(notificationId, { fileName: file.name, ...failure });
    }
  }

  /**
   * Push a synthetic 'cancelled' rejectedFiles entry tagged with the owning
   * batch notification, so the Results modal and the BatchCard's
   * cancelledCount reflect the cancellation. Idempotent — skipped when
   *   - the upload isn't part of a batch (no notification to tag with), or
   *   - status is 'success' (it actually completed, not a cancel), or
   *   - status is 'error' (the error path already pushed a 'failed' entry).
   *
   * Separated from clearUpload so cancelBatch can call it BEFORE its async
   * backend-delete await — that way the state machine sees cancelledCount>0
   * immediately and lands on 'cancelled-all' without a flicker through
   * 'done-success'. Other callers go through clearUploadAsCancellation,
   * which keeps the synthesize+clear pair atomic.
   */
  @action
  private synthesizeCancelledRejection(uuid: string): void {
    const uploadStatus = this.uploadStatus[uuid];
    if (!uploadStatus) return;
    if (uploadStatus.status === 'success' || uploadStatus.status === 'error') return;
    const owning = notificationStore.notifications.find(
      (n): n is UploadFileNotification => n.type === 'upload-file' && n.uploadUuids.includes(uuid),
    );
    if (!owning) return;
    this.rejectedFiles.push({
      file: uploadStatus.file,
      reason: 'Cancelled by user',
      category: 'other',
      notificationId: owning.id,
      status: 'cancelled',
    });
  }

  /**
   * Clear the upload from the manager without deleting the media. Pure
   * cleanup — aborts the upload, drops the entry from observable state, and
   * optionally toasts. Use clearUploadAsCancellation if you also want the
   * upload tagged as user-cancelled in rejectedFiles.
   */
  @action
  clearUpload(uuid: string, showSnackbar: boolean = true) {
    const uploadStatus = this.uploadStatus[uuid];
    if (!uploadStatus) return;

    if (uploadStatus.abortController) {
      uploadStatus.abortController.abort();
    }

    const mediaId = this.getMediaIdFromUuid(uuid);

    runInAction(() => {
      delete this.uploadStatus[uuid];
      this.uploadIdArray = this.uploadIdArray.filter((id) => id !== uuid);
      if (mediaId) delete this.mediaIdToLocalUUID[mediaId];
    });

    // Notify user this upload was removed from the queue (toast)
    if (showSnackbar) {
      showToast('upload-cleared', { filename: uploadStatus.file.name });
    }

    return mediaId;
  }

  /** Synthesize the cancelled rejectedFiles entry and clear the upload in
   * one call. The common path for "user explicitly cancelled this mid-flight
   * upload" — covers the library-grid X button, bulk cancel, etc. */
  @action
  clearUploadAsCancellation(uuid: string, showSnackbar: boolean = true) {
    this.synthesizeCancelledRejection(uuid);
    return this.clearUpload(uuid, showSnackbar);
  }

  /**
   * True while this client still has an upload **in flight** for `mediaId` —
   * an entry exists and has not failed or been cancelled.
   *
   * The distinction matters to the status poller: a backend media sitting at
   * `empty`/`uploading` only ever advances because THIS client finishes the
   * upload, so once the upload is dead the media is a leftover and polling for
   * it would never terminate.
   *
   * Replaces the broader `hasUploadForMedia`, which stayed true for
   * failed/cancelled entries. Its only remaining caller had become a guard in
   * front of `clearUploadForMedia`, which already makes the same check itself.
   */
  hasLiveUploadForMedia(mediaId: string): boolean {
    const uuid = this.mediaIdToLocalUUID[mediaId];
    if (!uuid) return false;
    const status = this.uploadStatus[uuid]?.status;
    return status === 'queued' || status === 'uploading' || status === 'success';
  }

  /**
   * Deletes the backend media row associated with an upload, if one was
   * created during prepareToUpload. Used by cancel flows without folder-
   * store context (e.g. UploadStatus panel cancel-batch). The library-grid
   * X-button path goes through folderStore.deleteMedia instead — same
   * DELETE endpoint, but also syncs folder local state, which we don't have
   * here. 404s are silently swallowed (already deleted); other errors are
   * logged but don't throw so one failure in a batch doesn't abort siblings.
   */
  async deleteBackendMediaForUpload(uuid: string): Promise<void> {
    const status = this.uploadStatus[uuid];
    if (!status) return;
    if (status.status === 'success') return; // file is what the user wanted
    const mediaId = this.getMediaIdFromUuid(uuid);
    if (!mediaId) return; // upload never got past prepareToUpload
    const libraryId = status.destination.libraryId;
    try {
      await userStore.apiWithAuth(`/library/${libraryId}/media/${mediaId}`, {
        method: 'DELETE',
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return;
      // TODO: surface non-404 backend-delete failures to the user. Today
      // the panel will pretend cancel succeeded (the row disappears
      // locally) while the media row stays on the server. Matches the
      // grid X-button path's existing best-effort behaviour, but worth
      // a proper toast + retry hook later. Tracked in
      // .claude/scratchpad/feat-upload-status-failure-tracking.md.
      console.warn(`Failed to delete backend media ${mediaId} during cancel:`, e);
    }
  }

  /** Remove upload-phase rejections the user dismissed, and queue the backend
   * media each failed one left behind for deletion. The flush re-checks the
   * media first, so one that landed after all is kept. */
  @action
  dismissRejectedFiles(predicate: (rejected: RejectedFile) => boolean) {
    const dismissed = this.rejectedFiles.filter(predicate);
    this.rejectedFiles = this.rejectedFiles.filter((r) => !predicate(r));
    for (const { createdMedia } of dismissed) {
      if (createdMedia) this.enqueueMediaDeletion(createdMedia.libraryId, createdMedia.mediaId);
    }
  }

  /** Queue an orphaned failed-upload media for backend deletion. Fires the
   * DELETE best-effort now and retries on `online`; offline failures stay
   * queued. The caller is expected to have already removed the row locally. */
  @action
  enqueueMediaDeletion(libraryId: string, mediaId: string) {
    if (this.pendingMediaDeletions.some((p) => p.mediaId === mediaId)) return;
    this.pendingMediaDeletions.push({ libraryId, mediaId });
    void this.flushPendingMediaDeletions();
  }

  @action
  private dropPendingDeletion(mediaId: string) {
    this.pendingMediaDeletions = this.pendingMediaDeletions.filter((p) => p.mediaId !== mediaId);
  }

  /** Re-entrancy guard: enqueue fires a flush and so does the `online` event, so
   *  two flushes can overlap — each snapshotting the queue and issuing a duplicate
   *  DELETE for the same id. Transient control state, not observable. */
  private flushingDeletions = false;

  /** Attempt every deferred deletion; drop the ones that succeed (or 404 = already
   * gone), keep the rest queued for the next attempt. Runs on enqueue and on the
   * window `online` event. */
  flushPendingMediaDeletions = async () => {
    if (this.flushingDeletions) return;
    this.flushingDeletions = true;
    try {
      // Re-check after each pass: a deletion enqueued *during* an in-flight DELETE
      // would otherwise be stranded, because its own flush() call is blocked by the
      // guard above. `attempted` bounds each id to one try per run, so ids that fail
      // and stay queued (offline/timeout) don't loop forever — they wait for the
      // next enqueue / `online` event, which starts a fresh run.
      const attempted = new Set<string>();
      for (;;) {
        const pending = this.pendingMediaDeletions.filter((p) => !attempted.has(p.mediaId));
        if (pending.length === 0) break;
        for (const { libraryId, mediaId } of pending) {
          attempted.add(mediaId);
          try {
            // SAFETY: a deferred deletion can outlive the upload it was queued
            // for, so re-check the media's CURRENT state before deleting. We only
            // delete a media we can prove is an abandoned orphan of OUR upload:
            //  - 'empty' — start succeeded, no bytes committed, finish never
            //    happened (same thing empty-media replace deletes); or
            //  - 'uploading' BUT only while this tab still tracks it as a failed
            //    upload we started. We own it then — dedup means no other tab holds
            //    this media id (a second start with the same uniqueId 409s and
            //    bails) — so the stale 'uploading' is our dead upload, not a live
            //    one elsewhere. (A landed upload is 'queued'/'processing'/'completed'
            //    — handled below — never 'uploading', so this can't hit a good file.)
            // Anything else, cancel the queued deletion.
            const media = await userStore.apiWithAuth<Media>(
              `/library/${libraryId}/media/${mediaId}`,
              { signal: signalWithTimeout(undefined, UPLOAD_REQUEST_TIMEOUT_MS) },
            );
            const status = media?.status;

            // Do we still track this as a failed upload THIS tab started? If so we
            // own it (see above). uploadStatus 'error'/'cancelled' survives to flush
            // (clearUploadForMedia only clears 'success').
            const ownedUuid = this.mediaIdToLocalUUID[mediaId];
            const isOwnedFailedUpload =
              !!ownedUuid &&
              (this.uploadStatus[ownedUuid]?.status === 'error' ||
                this.uploadStatus[ownedUuid]?.status === 'cancelled');

            if (status === 'empty' || (status === 'uploading' && isOwnedFailedUpload)) {
              await userStore.apiWithAuth(`/library/${libraryId}/media/${mediaId}`, {
                method: 'DELETE',
                signal: signalWithTimeout(undefined, UPLOAD_REQUEST_TIMEOUT_MS),
              });
              this.dropPendingDeletion(mediaId);
              this.telemetry.breadcrumb('deferred media deletion flushed', { mediaId, status });
            } else if (status && LANDED_MEDIA_STATUSES.includes(status)) {
              // The upload actually landed (false-negative failure: client said
              // 'error', backend finished). Cancel the deletion, re-surface the
              // recovered file in the library, and flag the near-miss — we just
              // prevented a real data loss.
              this.dropPendingDeletion(mediaId);
              this.refreshLibrary(libraryId);
              this.telemetry.reportDeferredDeleteSkipped({ mediaId, libraryId, status });
            } else if (status) {
              // 'uploading' we can't prove we own (e.g. after a reload cleared our
              // upload tracking — it may be a live upload elsewhere), or
              // 'errored'/'cancelled' (a file the user may still act on). Never
              // auto-delete these; cancel the queued deletion.
              this.dropPendingDeletion(mediaId);
              this.telemetry.breadcrumb('deferred delete skipped — media not a deletable orphan', {
                mediaId,
                status,
              });
            } else {
              // Status field absent — can't classify (backend fields aren't
              // guaranteed present). Don't act on an unconfirmed state; leave it
              // queued for a clearer read on a later flush.
              this.telemetry.breadcrumb('deferred delete deferred — media status indeterminate', {
                mediaId,
              });
            }
          } catch (e) {
            // 404 = already deleted; drop it. Anything else (offline/timeout on the
            // GET or DELETE) stays queued and is retried on the next `online` event —
            // never delete blindly when we couldn't confirm the media's state.
            if (e instanceof ApiError && e.status === 404) this.dropPendingDeletion(mediaId);
          }
        }
      }
    } finally {
      this.flushingDeletions = false;
    }
  };

  /**
   * Cancel every in-flight upload that belongs to a batch notification.
   * Deletes each upload's backend media (if one was created) before clearing
   * the local state — otherwise ghost 'empty' media rows pile up on the
   * server. Files that already uploaded successfully are left alone — the
   * designer spec says "already uploaded files remain."
   */
  async cancelBatch(notificationId: string): Promise<void> {
    const notification = notificationStore.notifications.find((n) => n.id === notificationId);
    if (!notification || notification.type !== 'upload-file') return;

    const uuidsToCancel: string[] = [];
    const mediaIdsToPrune: string[] = [];
    for (const uuid of notification.uploadUuids) {
      const status = this.uploadStatus[uuid];
      if (!status || status.status === 'success') continue;
      uuidsToCancel.push(uuid);
      const mediaId = this.getMediaIdFromUuid(uuid);
      if (mediaId) mediaIdsToPrune.push(mediaId);
    }
    if (uuidsToCancel.length === 0) return;

    // Mark this batch as user-cancelled so the still-running uploadFiles()
    // suppresses its `upload-batch-complete` toast — otherwise the user
    // sees both "Import cancelled" (from this method) and "N files failed
    // to upload" (from uploadFiles' Promise.allSettled resolving with the
    // AbortErrors clearUpload triggers).
    this.cancelledBatchIds.add(notificationId);

    // Push the notification to its terminal state up front, in one
    // synchronous batch with the cancelled rejectedFiles entries:
    //   - markBatchCancelled prunes mediaIds and (when nothing survived)
    //     pushes uploadProgress + processingProgress to 1 so isDone fires.
    //   - the rejectedFiles synthesis here, normally done inside clearUpload,
    //     is moved earlier so cancelledCount > 0 by the time deriveBatchState
    //     re-evaluates. Without this the state machine would briefly read
    //     isDone=true, cancelledCount=0, succeededCount=0 and land on
    //     'done-total-failure' between this point and the async clearUpload
    //     calls below — visible as a flicker of "Upload failed" on the
    //     card before "Upload canceled" finally lands.
    // clearUpload is told to skip its own synthesis so we don't double up.
    const cancelledSet = new Set(mediaIdsToPrune);
    const survivedMediaIds: string[] = [];
    const survivedMediaTitles: string[] = [];
    for (let i = 0; i < notification.mediaIds.length; i++) {
      const id = notification.mediaIds[i];
      if (cancelledSet.has(id)) continue;
      survivedMediaIds.push(id);
      survivedMediaTitles.push(notification.mediaTitles[i] ?? 'Untitled');
    }
    runInAction(() => {
      for (const uuid of uuidsToCancel) {
        this.synthesizeCancelledRejection(uuid);
      }
      notificationStore.markBatchCancelled(notificationId, survivedMediaIds, survivedMediaTitles);
    });

    // Backend delete BEFORE clearUpload — the mediaId lives on the
    // uploadStatus entry which clearUpload will remove. Synthesis already
    // happened above (pre-await) so we use plain clearUpload, not
    // clearUploadAsCancellation, to avoid duplicate rejectedFiles entries.
    await Promise.allSettled(uuidsToCancel.map((uuid) => this.deleteBackendMediaForUpload(uuid)));
    for (const uuid of uuidsToCancel) {
      this.clearUpload(uuid, false);
    }
    // Tell any mounted LibraryFolderStore to drop these media ids from its
    // local file list immediately, rather than waiting for the status-poll
    // cycle to notice the 404.
    if (mediaIdsToPrune.length > 0) {
      mediaDeletedEvent.emit({
        libraryId: notification.libraryId,
        mediaIds: mediaIdsToPrune,
      });
    }
    this.refreshLibrary(notification.libraryId);
    showToast('media-upload-cancelled');
  }

  /**
   * Retry a single upload-phase failure. Finds the matching rejectedFiles entry
   * (by notificationId + File identity), removes it, and re-uploads just that
   * file into the notification's library + folder (folderPath is persisted on
   * the notification, so retries land in the original folder rather than the
   * library root).
   */
  async retryRejectedFile(notificationId: string, file: File): Promise<void> {
    const notification = notificationStore.notifications.find((n) => n.id === notificationId);
    if (!notification) return;
    if (notification.type !== 'upload-file' && notification.type !== 'add-media-via-url') return;

    const idx = this.rejectedFiles.findIndex(
      (r) =>
        r.file === file &&
        r.notificationId === notificationId &&
        (r.status ?? 'failed') === 'failed',
    );
    if (idx < 0) return;

    runInAction(() => {
      this.rejectedFiles.splice(idx, 1);
    });
    const destination = { libraryId: notification.libraryId, folderPath: notification.folderPath };
    await this.uploadFiles(
      [file],
      destination,
      'file-picker',
      undefined,
      `Retry of ${file.name}`,
      this.resumableUploadsFor([file], destination),
    );
  }

  /**
   * Retry a single processing-phase failure by hitting the backend retry
   * endpoint for one media. Removes the ID from the notification's errored set
   * so the card updates immediately.
   */
  async retryProcessingFailure(notificationId: string, mediaId: string): Promise<void> {
    const notification = notificationStore.notifications.find((n) => n.id === notificationId);
    if (!notification) return;
    if (notification.type !== 'upload-file' && notification.type !== 'add-media-via-url') return;
    if (!notification.erroredMediaIds.includes(mediaId)) return;

    try {
      await userStore.apiWithAuth<Media>(
        `/library/${notification.libraryId}/media/${mediaId}/retry`,
        { method: 'POST' },
      );
      runInAction(() => {
        notification.erroredMediaIds = notification.erroredMediaIds.filter((id) => id !== mediaId);
      });
    } catch (e) {
      // Leave the ID in erroredMediaIds so the card stays in its failed state
      // instead of flickering through "retrying" and back.
      console.warn(`Retry failed for media ${mediaId}:`, e);
    }
  }

  /**
   * Retry every upload-phase failure for a batch. Re-queues the File objects
   * (preserved on rejectedFiles) via uploadFiles(), targeting the same
   * library + folder as the original batch (folderPath is persisted on the
   * notification).
   */
  async retryRejectedFiles(notificationId: string): Promise<void> {
    const notification = notificationStore.notifications.find((n) => n.id === notificationId);
    if (!notification) return;
    if (notification.type !== 'upload-file' && notification.type !== 'add-media-via-url') return;

    const toRetry = this.rejectedFiles.filter(
      (r) => r.notificationId === notificationId && (r.status ?? 'failed') === 'failed',
    );
    if (toRetry.length === 0) return;

    const files = toRetry.map((r) => r.file);
    runInAction(() => {
      this.rejectedFiles = this.rejectedFiles.filter((r) => !toRetry.includes(r));
    });

    const retryTitle =
      files.length === 1 ? `Retry of ${files[0].name}` : 'Retry of all failed files';
    const destination = { libraryId: notification.libraryId, folderPath: notification.folderPath };
    await this.uploadFiles(
      files,
      destination,
      'file-picker',
      undefined,
      retryTitle,
      this.resumableUploadsFor(files, destination),
    );
  }

  /**
   * Retry processing-phase failures for a single batch by hitting the backend
   * retry endpoint for each errored media. Only removes IDs whose retry call
   * succeeded — failed retries stay in `erroredMediaIds` so the card stays in
   * its failed state instead of flickering through "retrying" and back.
   */
  async retryProcessingFailures(notificationId: string): Promise<void> {
    const notification = notificationStore.notifications.find((n) => n.id === notificationId);
    if (!notification) return;
    if (notification.type !== 'upload-file' && notification.type !== 'add-media-via-url') return;

    const erroredIds = [...notification.erroredMediaIds];
    if (erroredIds.length === 0) return;

    const libraryId = notification.libraryId;
    const results = await Promise.allSettled(
      erroredIds.map((mediaId) =>
        userStore.apiWithAuth<Media>(`/library/${libraryId}/media/${mediaId}/retry`, {
          method: 'POST',
        }),
      ),
    );
    const succeededIds = new Set(erroredIds.filter((_, i) => results[i].status === 'fulfilled'));
    if (succeededIds.size === 0) return;
    runInAction(() => {
      notification.erroredMediaIds = notification.erroredMediaIds.filter(
        (id) => !succeededIds.has(id),
      );
    });
  }

  /**
   * Retry all failures for one or all batches. Runs upload-phase + processing-
   * phase retries in parallel per batch.
   */
  async retryAllFailures(notificationId?: string): Promise<void> {
    const batches = notificationId
      ? notificationStore.notifications.filter((n) => n.id === notificationId)
      : notificationStore.notifications;
    await Promise.all(
      batches
        .filter(
          (n): n is UploadFileNotification | AddMediaViaUrlNotification =>
            n.type === 'upload-file' || n.type === 'add-media-via-url',
        )
        .flatMap((n) => [this.retryRejectedFiles(n.id), this.retryProcessingFailures(n.id)]),
    );
  }

  /**
   * Cross-references `rejectedFiles` (upload-phase + cancelled) and the
   * notification's `erroredMediaIds` (populated by polling in Notifications.ts)
   * for a given batch notification.
   *
   * Upload failures come in two lists. `uploadFailures` is the batch's outcome
   * (persisted on the notification, never shrinks), which the BatchCard counts
   * and derives its state from. `pendingUploadFailures` is the subset still in
   * `rejectedFiles` — still holding the File to retry, gone after a reload or
   * once the user retries or removes it — which the Results modal lists.
   *
   * `processingFailures[].reason` is intentionally a hardcoded generic string —
   * the backend does not expose a per-media `failReason`, and we don't expect
   * it to. Surfacing pipeline internals would leak "how the sausage is made"
   * to users.
   *
   * Pass `folderFiles` to resolve errored media IDs to full Media objects (for
   * the Results modal's thumbnail + filename). Omit it when only counts are
   * needed (BatchCard subtitle).
   */
  getBatchFailures(
    notification: UploadFileNotification | AddMediaViaUrlNotification,
    folderFiles?: Media[],
  ): BatchFailures {
    return summarizeBatchFailures(notification, this.rejectedFiles, folderFiles);
  }

  /**
   * Silently clears the upload entry for a given media ID.
   * Called when the backend confirms it has the file (status moved past 'empty'),
   * so the upload manager no longer needs to track it.
   */
  @action
  clearUploadForMedia(mediaId: string) {
    const uuid = this.mediaIdToLocalUUID[mediaId];
    if (!uuid) return;
    const uploadStatus = this.uploadStatus[uuid];
    if (!uploadStatus || uploadStatus.status !== 'success') return;
    runInAction(() => {
      delete this.uploadStatus[uuid];
      this.uploadIdArray = this.uploadIdArray.filter((id) => id !== uuid);
      delete this.mediaIdToLocalUUID[mediaId];
    });
  }

  /**
   * Returns the media IDs and upload IDs that need to be cleaned up.
   * This allows the caller to perform the cleanup actions without the UploadManager
   * needing to know about the LibraryFolderStore.
   */
  @action
  getMediaIdsToCleanUp(type: 'all' | 'queuedAndUploading'): {
    mediaIds: string[];
    uploadIds: string[];
  } {
    const mediaIds: string[] = [];
    const uploadIdsToClear: string[] = [];

    this.uploadIdArray.forEach((uuid) => {
      const uploadStatus = this.uploadStatus[uuid];
      const mediaId = this.getMediaIdFromUuid(uuid);

      if (type === 'all') {
        // For 'all', we clear everything but only delete media if not successful
        if (mediaId && uploadStatus.status !== 'success') {
          mediaIds.push(mediaId);
        }
        uploadIdsToClear.push(uuid);
      } else if (type === 'queuedAndUploading') {
        // For 'queuedAndUploading', only process queued or uploading items
        if (uploadStatus.status === 'queued' || uploadStatus.status === 'uploading') {
          if (mediaId) {
            mediaIds.push(mediaId);
          }
          uploadIdsToClear.push(uuid);
        }
      }
    });

    return { mediaIds, uploadIds: uploadIdsToClear };
  }

  /**
   * Clears specific uploads from the upload manager.
   * This should be called after the media has been deleted from the folder store.
   */
  @action
  clearSpecificUploads(uuids: string[]) {
    uuids.forEach((uuid) => this.clearUploadAsCancellation(uuid, false));
    // Notify user all selected uploads were cleared (toast)
    if (uuids.length > 0) {
      showToast('uploads-all-cleared');
    }
  }

  /**
   * Simplified method to clear uploads and clean up related media atomically.
   * This encapsulates the entire flow: get media IDs, delete media, clear uploads, and refresh library.
   *
   * @param type - Whether to clear 'all' uploads or just 'queuedAndUploading'
   * @param folderStore - The folder store to use for deleting media and determining which library to refresh
   *
   * Usage:
   * uploads.clearUploadsAndCleanup('queuedAndUploading', folder);
   */
  @action
  async clearUploadsAndCleanup(
    type: 'all' | 'queuedAndUploading',
    folderStore: LibraryFolderStore,
  ): Promise<void> {
    const { mediaIds, uploadIds } = this.getMediaIdsToCleanUp(type);

    // Delete media from the folder store
    await Promise.all(mediaIds.map((id) => folderStore.deleteMedia(id)));

    // Clear uploads from the manager
    this.clearSpecificUploads(uploadIds);

    this.refreshLibrary(folderStore.library.libraryId);
  }

  /**
   * UUID-based method for clearing specific uploads and cleaning up related media.
   *
   * If folderStore is provided, handles everything atomically (delete + clear + refresh).
   * If folderStore is omitted, returns media IDs and cleanup function for manual handling.
   *
   * @param uuids - Array of upload UUIDs to clear
   * @param options - Configuration options
   * @param options.folderStore - Optional folder store to handle deletion atomically
   * @param options.mode - 'individual' for per-upload snackbars, 'bulk' for single snackbar
   * @param options.showSnackbar - Whether to show snackbar notifications
   *
   * Usage (new simplified approach):
   * await uploads.clearAndCleanupUploads([uuid], {
   *   folderStore: folder,
   *   mode: 'individual',
   *   showSnackbar: true
   * });
   *
   * Usage (legacy approach for backward compatibility):
   * const { mediaIdsToDelete, cleanup } = uploads.clearAndCleanupUploads([uuid], {
   *   mode: 'individual',
   *   showSnackbar: true
   * });
   * mediaIdsToDelete.forEach(id => folder.deleteMedia(id));
   * cleanup();
   */
  @action
  async clearAndCleanupUploads(
    uuids: string[],
    options?: {
      folderStore?: LibraryFolderStore;
      mode?: 'individual' | 'bulk';
      showSnackbar?: boolean;
    },
  ): Promise<void | { mediaIdsToDelete: string[]; cleanup: () => void }> {
    const { folderStore, mode = 'bulk', showSnackbar = true } = options || {};
    const mediaIds: string[] = [];

    uuids.forEach((uuid) => {
      const uploadStatus = this.uploadStatus[uuid];
      if (!uploadStatus) return;

      const mediaId = this.getMediaIdFromUuid(uuid);
      if (mediaId && uploadStatus.status !== 'success') {
        mediaIds.push(mediaId);
      }
    });

    // If folder store provided, handle everything atomically
    if (folderStore) {
      // Delete media from the folder store
      await Promise.all(mediaIds.map((id) => folderStore.deleteMedia(id)));

      // Clear uploads from the manager
      if (mode === 'individual') {
        uuids.forEach((uuid) => this.clearUploadAsCancellation(uuid, showSnackbar));
      } else {
        this.clearSpecificUploads(uuids);
      }

      this.refreshLibrary(folderStore.library.libraryId);

      return;
    }

    // Legacy return for backward compatibility
    return {
      mediaIdsToDelete: mediaIds,
      cleanup: () => {
        if (mode === 'individual') {
          // Clear each upload individually with snackbar control
          uuids.forEach((uuid) => this.clearUploadAsCancellation(uuid, showSnackbar));
        } else {
          // Clear all uploads at once with bulk snackbar
          this.clearSpecificUploads(uuids);
        }
      },
    };
  }

  /**
   * Clears successful uploads from the store. Only clears entries whose mediaId is in confirmedMediaIds
   * (media confirmed in library and past "empty"), so we don't remove the UUID until the media is alive.
   * If confirmedMediaIds is omitted, clears all success entries (e.g. when caller cannot provide the set).
   */
  @action
  clearSuccessfulUploads(confirmedMediaIds?: Set<string>) {
    this.uploadIdArray.forEach((uuid) => {
      const uploadStatus = this.uploadStatus[uuid];
      if (uploadStatus.status !== 'success') return;
      const mediaId = this.getMediaIdFromUuid(uuid);
      if (confirmedMediaIds != null && (mediaId == null || !confirmedMediaIds.has(mediaId))) {
        return;
      }
      this.clearUpload(uuid, false);
    });
  }

  isProgressing(fileUploadStatus: FileUploadStatus): boolean {
    return ['queued', 'uploading'].includes(fileUploadStatus.status);
  }

  isQueued(fileUploadStatus: FileUploadStatus): boolean {
    return this.isProgressing(fileUploadStatus) && fileUploadStatus.progress === 0;
  }

  isSuccess(fileUploadStatus: FileUploadStatus): boolean {
    return fileUploadStatus.status === 'success';
  }

  isFailed(fileUploadStatus: FileUploadStatus): boolean {
    return fileUploadStatus.status === 'error' || fileUploadStatus.status === 'cancelled';
  }

  isFinalized(fileUploadStatus: FileUploadStatus): boolean {
    return this.isSuccess(fileUploadStatus) || this.isFailed(fileUploadStatus);
  }
}

export const { Provider: UploadManagerProvider, useStore: useUploadManager } = createContext(
  UploadManager,
  {
    dispose: (store) => store.deinit(),
  },
);

// Expose UploadManager for debugging
declare global {
  interface Window {
    libraryManager: LibraryManager;
    uploadManager: UploadManager;
    UploadManager?: UploadManager;
  }
}

// Create a singleton instance for debugging
const debugUploadManager = new UploadManager();
window.UploadManager = debugUploadManager;
